/**
 * `ingestApi` — standalone RTK Query slice for the ingest pipelines
 * served by `axilon-api-service` under `/api/ontology/*` (with
 * unstructured zip uploads going directly to the proxy's
 * `/api/v1/ontology/documents/unzip`).
 *
 * Split off from `ontologyApi` so the IngestPage surface owns its
 * own cache + tag space (`'StructuredJobs'`, `'PlcJobs'`,
 * `'UnstructuredJobs'`, `'IpMappingJobs'`) instead of being lumped
 * into the catch-all `ontologyApi` tag soup. Shares
 * `ontologyBaseQuery` / `createOntologyHeaders` (API key + identity
 * Bearer + branch/site) with the other ontology slices via
 * `ontology-api-base.ts` and `ontology-api-auth.ts`.
 *
 * Cross-slice invalidation: the `applyStructuredDiff` mutation rewrites
 * the graph, so the shared cache-coherence bridge fans its fulfilled
 * action into every graph-derived RTK cache. The endpoint here only
 * declares its own `'StructuredJobs'` tag.
 *
 * Notable plumbing kept local to this file (and only this file):
 *   - `runXhrUpload` — XMLHttpRequest-backed `queryFn` helper that
 *     surfaces byte-level upload progress to the `ingest-upload-
 *     progress-slice`. RTK Query's default `fetchBaseQuery` is built
 *     on `fetch`, which has no way to surface request upload progress,
 *     so the upload mutations all route through this helper instead.
 *     Auth headers come from shared `createOntologyHeaders` (same as
 *     non-RTK fetch callers).
 *   - `abortIngestUpload(jobId)` — exported "Stop" hook for the
 *     IngestPage's per-row cancel button.
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import { normalizeAskUserQuestionFields } from '@atbash-labs/ontology-agent-wire'
import type { SoapAuthKind } from '@atbash-labs/ontology-core'
import type {
  PssAuthKind,
  PssDataOwnerRef,
  PssDiscoveredOwner,
  PssDiscoveredTable,
  PssDiscoverResponse,
  PssDiscoverySummary,
  PssJournalKinds
} from '@atbash-labs/ontology-ingest'
import type {
  IgnitionTagRegistrationProtocol,
  TagBatchCanaryAuthMode
} from '@atbash-labs/ontology-tag-registration'
import type { IngestRun, JobLogPage, ProxyConversationWithRuns } from '@atbash-labs/proxy-client'

import type { TaggedNode, TaggedRel } from '../branches/types'
import { getOntologyServiceBaseUrl } from '../../config'
import { clearUploadProgress, setUploadProgress } from '../../slices/ingest-upload-progress-slice'
import { PROXY_API_V1_URL, createOntologyHeaders, ontologyBaseQuery } from '../core'
import type {
  CheckExistingResponse,
  CreateIngestIntakeBody,
  DiffPreviewResponse,
  DiffUploadResponse,
  IngestIntake,
  IngestIntakeItem,
  ListIngestIntakesResponse,
  ListIngestIntakeItemsResponse,
  PatchIngestIntakeBody,
  TabularFileNameMatch,
  JobListItem,
  JobsPage,
  IpMappingJob,
  MTConnectJob,
  PIJob,
  CanaryJob,
  ExperionJob,
  SrsJob,
  SafetyManagerJob,
  TagMappingJob,
  TagMappingEnumJson,
  TagMappingLinkExclude,
  TagMappingPreviewResult,
  TagMappingSuggestResult,
  TagMappingTypeSummary,
  PlcJob,
  PlcJobDetail,
  StorageStatusResponse,
  StructuredJob,
  StructuredJobDetail,
  TagImportJob,
  TabularDumpJob,
  TabularDumpIssue,
  TabularDumpTablePreview,
  SqlJob,
  SoapJob,
  PssJob,
  BeginDocumentAdditionResult,
  CompleteDocumentAdditionResult,
  UnstructuredJob,
  UnzipUploadResponse,
  WorkOrdersJob
} from './types'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

export const INGEST_TAG_TYPES = [
  'StructuredJobs',
  'PlcJobs',
  'RoboticsJobs',
  'VisionJobs',
  'MTConnectJobs',
  'PIJobs',
  'CanaryJobs',
  'ExperionJobs',
  'SrsJobs',
  'SafetyManagerJobs',
  'TagMappingJobs',
  'UnstructuredJobs',
  'IpMappingJobs',
  'TagImportJobs',
  'TabularDumpJobs',
  'MappedTabularIngestJobs',
  'TabularConfigBuilds',
  'IngestMappingConfigs',
  'SqlJobs',
  'SoapJobs',
  'PssJobs',
  'WorkOrdersJobs',
  'IngestQuestions',
  'IngestConversation',
  'BranchIngestPaint',
  'IngestIntakes',
  'IngestIntakeItems',
  'SiteDocuments',
  'JobLogs'
] as const
export type IngestTagType = (typeof INGEST_TAG_TYPES)[number]

export interface GetJobLogsPageArg {
  jobId: string
  pageSize?: number
  pageEnd?: number
  query?: string
}

/** One object from proxy `GET /api/v1/ontology/documents?site_id=…`. */
export interface SiteDocument {
  fileName: string
  /** Proxy-derived leaf folder — unreliable for nested SCADA/PLC keys. */
  kind: string
  size: number
  lastModified: string
  /**
   * Opaque object key from the proxy list response. Prefer this for download
   * and preview — do not reconstruct storage paths client-side.
   */
  key?: string
}

/** Storage family used when listing with an explicit `kind` query param. */
export type SiteDocumentKind = 'project-roots' | 'plc' | 'documents' | 'ip-map' | 'tag-lists'

/**
 * One unstructured corpus file for the Ingest "Ingested files" tab.
 * Sourced from per-job `job-files` inventory (unzip lives under
 * `ingest-document-unzips/…`, not `site-ingest/…/documents`).
 */
export interface SiteUnstructuredFile {
  jobId: string
  path: string
  fileName: string
  status: string
  /** Unix epoch milliseconds from the job-files row. */
  updatedAtMs: number
}

/** Address for `DELETE /api/v1/ontology/documents`. */
export type DeleteIngestedFileArg = { key: string } | { jobId: string; path: string }

const SITE_UNSTRUCTURED_JOB_PAGE_SIZE = 100
const SITE_UNSTRUCTURED_JOB_MAX_PAGES = 3
const SITE_UNSTRUCTURED_JOB_CAP = 30

function basenamePath(path: string): string {
  const normalized = path.replace(/\\/g, '/').replace(/\/+$/, '')
  const i = normalized.lastIndexOf('/')
  return i >= 0 ? normalized.slice(i + 1) : normalized
}

/** Zip corpus + transform working trees are not operator-facing source docs. */
export function isUnstructuredCorpusNoisePath(path: string): boolean {
  const p = path.replace(/\\/g, '/')
  return (
    p === '.corpus' ||
    p.startsWith('.corpus/') ||
    p.includes('/.corpus/') ||
    p === '.transformed' ||
    p.startsWith('.transformed/') ||
    p.includes('/.transformed/')
  )
}

// ─── Constants + types shared with consumers (operator structured-doc upload) ────

export const STRUCTURED_DOCUMENT_TYPE_TO_SEMANTIC_TYPE = {
  IP: 'PLC ↔ SCADA mapping',
  ROBOTICS_PLC: 'Robotics ↔ PLC mapping',
  VISION_PLC: 'Vision system ↔ PLC mapping'
} as const

/**
 * Per-`StructuredDocumentType` backend route base under
 * `${ONTOLOGY_SERVICE_URL}`. Both pipelines share the two-step
 * begin + upload lifecycle; only the path segment differs.
 *   - `IP`           → existing IP-mapping ingest (Compute → Controller).
 *   - `ROBOTICS_PLC` → robotics ↔ PLC mapping ingest (PLCTag → RobotIOSignal).
 *   - `VISION_PLC`   → vision system ↔ PLC mapping from the controller L5X's
 *     I/O tree (Controller → VisionSystem → module PLCTags).
 */
const STRUCTURED_DOCUMENT_TYPE_TO_ROUTE_BASE = {
  IP: '/ingest/ip-mapping',
  ROBOTICS_PLC: '/ingest/robotics-plc-mapping',
  VISION_PLC: '/ingest/vision-plc-mapping'
} as const

export type StructuredDocumentType = keyof typeof STRUCTURED_DOCUMENT_TYPE_TO_SEMANTIC_TYPE

export interface StructuredDocumentUploadStubResult {
  accepted: true
  fileName: string
  documentType: StructuredDocumentType
  semanticType: (typeof STRUCTURED_DOCUMENT_TYPE_TO_SEMANTIC_TYPE)[StructuredDocumentType]
}

/**
 * Result returned by the real `uploadStructuredDocument` mutation
 * after the two-step begin + upload completes. Carries the created
 * ingest job id (the UI polls `/api/ontology/jobs/{jobId}` for state)
 * alongside the same `documentType` / `semanticType` metadata the
 * earlier stub exposed so existing call sites continue to work.
 */
export interface StructuredDocumentUploadResult {
  jobId: string
  branchId?: string
  fileName: string
  documentType: StructuredDocumentType
  semanticType: (typeof STRUCTURED_DOCUMENT_TYPE_TO_SEMANTIC_TYPE)[StructuredDocumentType]
}

/**
 * Response of the async work-orders tone upload. The endpoint persists the job
 * and kicks off the tone ingest in the background (202), so the mutation only
 * gets the job id back — progress + the final result surface on the job row in
 * the IngestPage (polled), not inline on this request.
 */
export interface WorkOrdersToneUploadStarted {
  jobId: string
}

export interface ConnectMTConnectAgentInput {
  agentUrl: string
  siteId: string
  gatewayId?: string
  /** Existing graph host used by Plant-provider re-scans. */
  computeUrn?: string
  username?: string
  password?: string
  /** Diff the current probe against the provider and stage missing Tags as deletions. */
  reconcileDataItems?: boolean
  groupId?: string
  ingestRunAction?: 'add_site' | 'update_site'
  siteName?: string
}

export interface ConnectMTConnectAgentResult {
  jobId: string
  branchId: string
  probe: {
    devices: number
    dataItems: number
    instanceId: string | null
    bufferSize: number | null
    deviceModelChangeTime: string | null
  }
}

export type PIIngestMode = 'standalone' | 'existing_tags'
/** Live-acquisition families an external historian (PI, Canary) can back. */
export type ExternalHistorySource = 'ignition' | 'opc'
export type PIHistorySource = ExternalHistorySource
/** External historians whose `existing_tags` ingest mode binds history to registered tags. */
export type ExternalHistoryProvider = 'pi' | 'canary'

/** Request fields shared by every external-history suggest / preview call. */
export interface ExternalHistoryMappingInput {
  baseUrl: string
  siteId: string
  /** Branch containing tags created earlier in this ingest run. */
  branchId?: string
  skipTlsVerify?: boolean
  nameFilter?: string
  historySources: ExternalHistorySource[]
  additionalContext?: string
  grammars?: Array<{
    id?: string
    label?: string
    rationale?: string
    a?: Array<{ name: string; type: string; regex: string }>
    b?: Array<{ name: string; type: string; regex: string }>
    enums?: TagMappingEnumJson
    join?: string[]
    aGrammar?: string
    bGrammar?: string
    enumMaps?: string
    exclude?: TagMappingLinkExclude[]
  }>
  manualLinks?: TagMappingLinkExclude[]
  groupId?: string
  ingestRunAction?: 'add_site' | 'update_site'
  siteName?: string
}

export interface PIHistoryMappingInput extends ExternalHistoryMappingInput {
  username: string
  password: string
}

export interface CanaryHistoryMappingInput extends ExternalHistoryMappingInput {
  authMode: CanaryAuthMode
  apiToken?: string
  username?: string
  password?: string
  rootPath?: string
}

export interface ConnectPIInput extends Partial<PIHistoryMappingInput> {
  baseUrl: string
  siteId: string
  ingestMode: PIIngestMode
  gatewayId?: string
  computeUrn?: string
  username?: string
  password?: string
  skipTlsVerify?: boolean
  nameFilter?: string
  groupId?: string
  ingestRunAction?: 'add_site' | 'update_site'
  siteName?: string
}

/** Canary shares PI's historian ingest modes and the proxy batch wire's auth modes. */
export type CanaryIngestMode = PIIngestMode
export type CanaryAuthMode = TagBatchCanaryAuthMode

export interface ConnectCanaryInput {
  baseUrl: string
  siteId: string
  ingestMode: CanaryIngestMode
  authMode: CanaryAuthMode
  apiToken?: string
  username?: string
  password?: string
  rootPath?: string
  nameFilter?: string
  skipTlsVerify?: boolean
  gatewayId?: string
  computeUrn?: string
  groupId?: string
  ingestRunAction?: 'add_site' | 'update_site'
  siteName?: string
}

export interface ConnectCanaryResult {
  jobId: string
  branchId?: string
  probe: {
    nodes: number
    tags: number
    warnings: number
  }
}

export interface ConnectPIResult {
  jobId: string
  branchId?: string
  probe: {
    dataServers: number
    points: number
    existingTags?: number
    approvedBindings?: number
  }
}

export interface ConnectSqlInput {
  engine: string
  server: string
  database: string
  username: string
  password: string
  siteId: string
  connectionName: string
  replaceExisting?: boolean
  tableNames?: string[]
  groupId?: string
  ingestRunAction?: 'add_site' | 'update_site'
  siteName?: string
}

export interface ConnectSqlResult {
  jobId: string
  branchId: string
  probe: {
    engine: string
    database: string
  }
}

export type { SoapAuthKind }

export interface SoapOperationPreview {
  operationId?: string
  name: string
  soapAction?: string
  inputMessage?: string
  outputMessage?: string
  documentation?: string
  binding?: string
  port?: string
  inputElement?: string
  outputElement?: string
  arguments?: unknown[]
}

export interface SoapContractPreview {
  endpointId?: string
  contractHash: string
  endpoint: {
    name?: string
    wsdlUrl?: string
    address?: string
    serviceName?: string
    targetNamespace?: string
    soapVersion?: string
    contractId?: string
  }
  operations: SoapOperationPreview[]
}

export interface PreviewSoapInput {
  endpointName: string
  wsdlUrl: string
  authKind: SoapAuthKind
  username?: string
  password?: string
  domain?: string
}

export interface ConnectSoapInput extends PreviewSoapInput {
  siteId: string
  enabledOperations: string[]
  expectedContractHash: string
  groupId?: string
  ingestRunAction?: 'add_site' | 'update_site'
  siteName?: string
}

export interface ConnectSoapResult {
  jobId: string
  branchId: string
  endpointId: string
  contractHash: string
  operations: SoapOperationPreview[]
}

export type {
  PssAuthKind,
  PssDataOwnerRef,
  PssDiscoveredOwner,
  PssDiscoveredTable,
  PssDiscoverResponse,
  PssDiscoverySummary,
  PssJournalKinds
}

/** Hexagon PlantState Suite discovery probe (BFF `/ingest/pss/discover`). Credentials are never persisted. */
export interface DiscoverPssInput {
  serviceUrl: string
  authKind: PssAuthKind
  username?: string
  password?: string
  domain?: string
  lookbackHours?: number
  ownerFilter?: string[]
  maxOwners?: number
}

export interface ConnectExperionErdbInput {
  server: string
  database: string
  /**
   * Optional EMDB (enterprise/PCN) catalogue on the same SQL login. Omit or
   * send an empty string to skip it; the connect route probes it fail-soft and
   * still ingests the ERDB when the EMDB is unreachable or empty.
   */
  emdbDatabase?: string
  username: string
  password: string
  siteId: string
  siteName?: string
  computeUrn?: string
  groupId?: string
  ingestRunAction?: 'add_site' | 'update_site'
}

export interface ConnectSafetyManagerDbInput {
  server: string
  database: string
  username: string
  password: string
  siteId: string
  siteName?: string
  systemId?: string
  groupId?: string
  ingestRunAction?: 'add_site' | 'update_site'
}

export interface ConnectSafetyManagerDbResult {
  jobId: string
  branchId: string
  probe: {
    controllerCount: number
    schemaVersion?: string
    database: string
    server: string
  }
}

export interface ConnectExperionErdbResult {
  jobId: string
  branchId: string
  probe: {
    strategyCount: number
    database: string
    server: string
  }
  /** EMDB probe outcome — loaded with a strategy count, or skipped with a reason. */
  emdb?: { database: string; strategyCount: number } | { skipped: true; reason: string }
}

/**
 * One open `ask_user_question` from the unstructured-ingest agent. The agent
 * ingest page polls these per job and posts an answer to unblock the run.
 */
export interface PendingIngestQuestion {
  /** `agent_writes` row id — the key for posting the answer. */
  writeId: string
  question: string
  /** Suggested answers offered as quick-pick buttons (free text still allowed). */
  options?: string[]
  /** Extra context the agent attached to the question. */
  context?: string
  /** ISO timestamp the question was asked. */
  askedAt: string
  /** Which agent asked (`ingest_agent` lead, parser, reconciler, …). */
  agentKind?: string
  /** Child dispatch id when a section parser / reconciler asked. */
  dispatchId?: string
  /** Section slug when a section parser asked. */
  sectionId?: string
  /** Nested `agent_runs.id` when available. */
  agentRunId?: string
}

/**
 * One answered `ask_user_question`, kept as quiet history in the Q&A sidebar.
 */
export interface AnsweredIngestQuestion {
  writeId: string
  question: string
  options?: string[]
  context?: string
  /** The operator's recorded answer (`result.answer`). */
  answer: string
  /** ISO timestamp the question was originally asked. */
  askedAt: string
  /** ISO timestamp the answer was recorded. */
  answeredAt: string
  agentKind?: string
  dispatchId?: string
  sectionId?: string
  agentRunId?: string
}

/**
 * One operator-initiated message — unsolicited input the operator typed into
 * the conversation (not an answer to a question), kept inline in the thread.
 */
export interface IngestOperatorMessage {
  writeId: string
  message: string
  /** ISO timestamp the message was sent (queued). Thread sort key and visible clock. */
  sentAt: string
  /**
   * ISO timestamp the agent drained the message (`completedAt` on the write).
   * Marks the end of the leftover-CoT window so unread-turn logs stay above
   * the note and the turn that actually read it stays below. The visible clock
   * and thread sort key are `sentAt`.
   */
  deliveredAt?: string
  /** True while the message is still queued (`processing`) — cancel/edit/force apply. */
  pending: boolean
  /** Present when the message targets a child dispatch (not the lead). */
  dispatchId?: string
  agentKind?: string
}

/** The full conversation thread for one ingest job. */
export interface IngestQuestionsResult {
  pending: PendingIngestQuestion[]
  answered: AnsweredIngestQuestion[]
  /** Operator-initiated messages, oldest first. */
  messages: IngestOperatorMessage[]
}

/** The agent's per-job scratchpad notes (markdown), read from MemoryFs. */
export interface IngestScratchpad {
  content: string
  etag?: string
  lastModified?: string
}

/**
 * One entry of the job's section catalog — an agent-defined workstream.
 * Merged sections stay in the catalog for provenance history; their graph
 * nodes already point at the survivor.
 */
export interface IngestSection {
  id: string
  name: string
  description: string
  status: 'active' | 'merged' | 'skipped'
  mergedInto?: string
  skipReason?: string
}

/** One row of the job's server-seeded file inventory. */
export interface IngestJobFile {
  jobId: string
  path: string
  sha256: string
  status: 'pending' | 'ingested' | 'skipped' | 'unsupported' | string
  duplicateOf?: string
  sectionId?: string
  nodeCount?: number
  reason?: string
  createdAt: number
  updatedAt: number
}

/**
 * "What this ingest job built", reconstructed from the durable graph-write log
 * (union of the batches' created/updated/deleted ids + relationship intents,
 * hydrated from the branch) rather than a whole-graph branch-vs-main diff.
 * Created-then-deleted churn is folded out server-side. `hasLog: false` means
 * the job predates the log — consumers fall back to the branch compare.
 * `sectionsByNode` maps a node id to the section(s) that wrote it; membership
 * lives here, not on the graph node. Relationship and job-level attribution
 * use the same durable write batches.
 */
/** Cheap write-log identity for paint polls (`COUNT` + `MAX(id)`). */
export interface IngestWriteLogWatermark {
  count: number
  maxId: number
}

/**
 * Session-local source citation from the write-log fold. Distinct from the
 * graph node's accumulated provenance — only evidence contributed by the
 * selected ingest fold.
 */
export interface IngestSessionEvidence {
  sourceJobId: string
  sourceFile: string
  sourcePage?: number
  sourceExcerpt?: string
  sourceKind?: string
  confidence?: number
  extractionModel?: string
  extractionMethod?: string
  extractedAt?: string
  sections: string[]
  jobLevel: boolean
}

export interface IngestModifications {
  hasLog: boolean
  nodes?: TaggedNode[]
  /** Net hydrated nodes created by this job; remaining `nodes` were touched. */
  createdNodeIds?: string[]
  /**
   * Net relationships created by this job; remaining `relationships` were
   * property-updated (touched). Absent on legacy payloads → treat all as added.
   */
  createdRelationshipIds?: string[]
  relationships?: TaggedRel[]
  /**
   * Placement ancestry for modified nodes (containment parents across Plant
   * paint hierarchies). Used only for rollup pills — not painted as session
   * relationship marks.
   */
  placementRelationships?: TaggedRel[]
  deletedNodes?: TaggedNode[]
  deletedRelationships?: TaggedRel[]
  counts?: {
    nodesAdded: number
    relsAdded: number
    nodesDeleted: number
    relsDeleted: number
  }
  /**
   * True when hydration was capped — marks cover the first N ids while
   * `counts` stay exact from the raw write-log fold.
   */
  truncated?: boolean
  sectionsByNode?: Record<string, string[]>
  sectionsByRelationship?: Record<string, string[]>
  /** Net-changed nodes written by at least one batch without a section. */
  jobLevelNodeIds?: string[]
  /** Net-changed relationships written by at least one batch without a section. */
  jobLevelRelationshipIds?: string[]
  /** Session-local source citations keyed by live node id. */
  sourceEvidenceByNode?: Record<string, IngestSessionEvidence[]>
}

/**
 * One entity's net property-level contribution within a changeset fold scope
 * (job, or job+section) — the read surface over the durable write-log deltas.
 * `created`/`deleted` carry no `changes` for a bare create (see
 * `IngestChangeset`); `deleted` may carry a `snapshot` of its removed props.
 */
export interface IngestChangesetEntry {
  entityKind: 'node' | 'rel'
  id: string
  rel?: { type: string; sourceId: string; targetId: string }
  label: string
  op: 'created' | 'updated' | 'deleted'
  changes?: Record<string, { before: unknown; after: unknown }>
  snapshot?: Record<string, unknown>
}

/**
 * "What this job (or one of its sections) actually changed, property by
 * property" — the fold of the durable write-log deltas. `hasLog: false` means
 * the job predates delta capture; batches written before capture landed carry
 * no deltas and simply fold to an empty `entries`, not `hasLog: false`.
 */
export interface IngestChangeset {
  hasLog: boolean
  entries?: IngestChangesetEntry[]
}

/** One job row in a branch-scoped modifications/changeset fold. */
export interface BranchIngestJobSummary {
  jobId: string
  kind: string
  domain: string
  conversationId?: string
  title?: string
  fileName?: string
  fileType?: string
  rowCount: number
  changeCount?: number
}

/** Branch union fold — same shape as job-scoped mods plus contributing jobs. */
export interface BranchIngestModifications extends IngestModifications {
  jobs?: BranchIngestJobSummary[]
}

/** Branch union changeset — same shape as job-scoped changeset plus contributing jobs. */
export interface BranchIngestChangeset extends IngestChangeset {
  jobs?: BranchIngestJobSummary[]
}

/** Wire shape of one pending-question row (proxy `agent_writes` projection). */
interface PendingQuestionWire {
  id: string
  summary?: string
  submittedAt?: string
  agentKind?: string
  params?: {
    question?: string
    /** May arrive as string[] or a JSON array string from older/polluted rows. */
    options?: string[] | string
    context?: string
    dispatchId?: string
    sectionId?: string
    agentRunId?: string
  }
}

/** Wire shape of one answered-question row (settled `agent_writes` projection). */
interface AnsweredQuestionWire extends PendingQuestionWire {
  completedAt?: string
  result?: { answer?: string }
}

/** Wire shape of one operator-message row (`user_message` projection). */
interface OperatorMessageWire {
  id: string
  summary?: string
  submittedAt?: string
  completedAt?: string
  state?: string
  agentKind?: string
  params?: {
    message?: string
    dispatchId?: string
    sectionId?: string
    agentRunId?: string
  }
}

/** POST `/messages` response — includes the durable write for optimistic reconcile. */
export interface SendIngestMessageResult {
  ok: boolean
  write?: OperatorMessageWire
}

/** Map a proxy/api-service operator-message write onto the UI message shape. */
export function toIngestOperatorMessage(
  write: OperatorMessageWire,
  fallbackMessage = ''
): IngestOperatorMessage {
  return {
    writeId: write.id,
    message: write.params?.message ?? write.summary ?? fallbackMessage,
    sentAt: write.submittedAt ?? '',
    ...(write.completedAt ? { deliveredAt: write.completedAt } : {}),
    pending: write.state === 'processing',
    ...(write.agentKind ? { agentKind: write.agentKind } : {}),
    ...(write.params?.dispatchId ? { dispatchId: write.params.dispatchId } : {})
  }
}

// ─── XHR upload plumbing ──────────────────────────────────────────

/**
 * Discriminated union matching RTK Query's `QueryReturnValue` shape so the
 * upload mutations can use this directly as their `queryFn` return.
 * Either `data` is present (success) or `error` is present (failure) —
 * never both, never neither. The error shape mirrors RTK's
 * `FetchBaseQueryError` (numeric status for HTTP errors, FETCH_ERROR with
 * a string `error` field for network failures) so existing error-handling
 * code in `extractErrorMessage` works unchanged.
 */
export type XhrUploadError =
  | { status: number; data: unknown }
  | { status: 'FETCH_ERROR'; data?: undefined; error: string }

export type XhrUploadResult<T = unknown> =
  | { data: T; error?: undefined; meta?: undefined }
  | { data?: undefined; error: XhrUploadError; meta?: undefined }

/**
 * Module-scope registry that lets the UI abort an in-flight upload XHR by
 * transfer key. Fresh Ingest uploads key by `jobId` (Stop-ingest). Document
 * additions key by `uploadId` so cancel-transfer does not collide with
 * job-level Stop. `runXhrUpload` lazily creates a controller at start and
 * releases it when the logical transfer finishes (or any request errors/aborts).
 */
const uploadAbortControllers = new Map<string, AbortController>()

function getOrCreateUploadAbortController(
  transferId: string,
  aliases: readonly string[]
): AbortController {
  let controller = uploadAbortControllers.get(transferId)
  if (!controller) {
    controller = new AbortController()
  }
  uploadAbortControllers.set(transferId, controller)
  for (const alias of aliases) uploadAbortControllers.set(alias, controller)
  return controller
}

function releaseIngestUploadController(
  transferId: string,
  aliases: readonly string[],
  controller: AbortController
): void {
  for (const id of [transferId, ...aliases]) {
    if (uploadAbortControllers.get(id) === controller) {
      uploadAbortControllers.delete(id)
    }
  }
}

/**
 * Abort an in-flight ingest upload by transfer key (`jobId` for fresh
 * uploads, `uploadId` for document additions). Idempotent; no-op when no
 * upload is registered.
 */
export function abortIngestUpload(transferId: string): void {
  uploadAbortControllers.get(transferId)?.abort()
}

export function runXhrUpload<T = unknown>(args: {
  url: string
  formData: FormData
  /**
   * Progress + abort key. Defaults to `jobId`. Document-addition transfers
   * pass `uploadId` so abort/progress stay transfer-scoped.
   */
  transferId?: string
  /** Additional progress/abort keys for the same logical transfer. */
  transferAliases?: string[]
  jobId: string
  dispatch: (action: ReturnType<typeof setUploadProgress | typeof clearUploadProgress>) => void
  signal?: AbortSignal
  /** Fires once when all request bytes have left the browser (XHR upload 100%). */
  onUploadBytesComplete?: () => void
  /** Aggregate progress when one logical transfer uses sequential requests. */
  progress?: {
    completedBytes: number
    currentFileBytes: number
    totalBytes: number
  }
  /** Keep progress + abort state for a following request in the same transfer. */
  finalizeTransfer?: boolean
}): Promise<XhrUploadResult<T>> {
  const { url, formData, jobId, dispatch, signal, onUploadBytesComplete, progress } = args
  const transferId = args.transferId ?? jobId
  const transferAliases = Array.from(
    new Set((args.transferAliases ?? []).filter((id) => id && id !== transferId))
  )
  const progressIds = [transferId, ...transferAliases]
  const finalizeTransfer = args.finalizeTransfer ?? true
  const uploadController = getOrCreateUploadAbortController(transferId, transferAliases)
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)

    const headers = createOntologyHeaders()
    headers.delete('Content-Type')
    headers.forEach((value, name) => xhr.setRequestHeader(name, value))

    const parseBody = (): unknown => {
      const text = xhr.responseText
      if (!text) return null
      try {
        return JSON.parse(text)
      } catch {
        return text
      }
    }

    let uploadBytesCompleteFired = false
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const uploadedBytes = progress
          ? progress.completedBytes +
            Math.round(
              progress.currentFileBytes * (event.total > 0 ? event.loaded / event.total : 0)
            )
          : event.loaded
        const totalBytes = progress?.totalBytes ?? event.total
        for (const id of progressIds) {
          dispatch(
            setUploadProgress({
              jobId: id,
              uploadedBytes,
              totalBytes
            })
          )
        }
        if (!uploadBytesCompleteFired && event.total > 0 && event.loaded >= event.total) {
          uploadBytesCompleteFired = true
          onUploadBytesComplete?.()
        }
      }
    }

    xhr.onload = () => {
      const data = parseBody()
      if (xhr.status >= 200 && xhr.status < 300) {
        if (finalizeTransfer) {
          for (const id of progressIds) dispatch(clearUploadProgress({ jobId: id }))
          releaseIngestUploadController(transferId, transferAliases, uploadController)
        }
        resolve({ data: data as T })
      } else {
        for (const id of progressIds) dispatch(clearUploadProgress({ jobId: id }))
        releaseIngestUploadController(transferId, transferAliases, uploadController)
        resolve({ error: { status: xhr.status, data } })
      }
    }

    xhr.onerror = () => {
      for (const id of progressIds) dispatch(clearUploadProgress({ jobId: id }))
      releaseIngestUploadController(transferId, transferAliases, uploadController)
      resolve({
        error: { status: 'FETCH_ERROR', error: 'Upload failed: network error' }
      })
    }

    xhr.onabort = () => {
      for (const id of progressIds) dispatch(clearUploadProgress({ jobId: id }))
      releaseIngestUploadController(transferId, transferAliases, uploadController)
      resolve({ error: { status: 'FETCH_ERROR', error: 'Upload aborted' } })
    }

    if (uploadController.signal.aborted) {
      xhr.abort()
      return
    }
    uploadController.signal.addEventListener('abort', () => xhr.abort(), {
      once: true
    })

    if (signal) {
      if (signal.aborted) {
        xhr.abort()
        return
      }
      signal.addEventListener('abort', () => xhr.abort())
    }

    xhr.send(formData)
  })
}

// ─── Local helpers used by the ingest endpoints ───────────────────

function createFormData(input: Record<string, string | Blob | undefined>) {
  const formData = new FormData()
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined) {
      formData.append(key, value)
    }
  }
  return formData
}

async function uploadCorpusFilesSequentially(args: {
  jobId: string
  files: File[]
  uploadId?: string
  /** Persisted on the unzip corpus as `.axilon/site.json` for site-scoped listing. */
  siteId?: string
  dispatch: (action: ReturnType<typeof setUploadProgress | typeof clearUploadProgress>) => void
  signal?: AbortSignal
  onUploadBytesComplete?: () => void
}): Promise<XhrUploadResult<UnzipUploadResponse>> {
  const { jobId, files, uploadId, siteId, dispatch, signal, onUploadBytesComplete } = args
  const transferId = uploadId ?? jobId
  const totalBytes = Math.max(
    files.reduce((sum, file) => sum + file.size, 0),
    1
  )
  let completedBytes = 0
  const responses: UnzipUploadResponse[] = []

  // One request per source file keeps ZIP extraction semantics intact
  // when a user selects line12.zip and line13.zip together. The shared
  // uploadId still makes an addition one logical, retry-safe batch.
  for (const [index, file] of files.entries()) {
    const finalRequest = index === files.length - 1
    const formData = new FormData()
    formData.append('job_id', jobId)
    if (uploadId) formData.append('upload_id', uploadId)
    if (siteId?.trim()) formData.append('site_id', siteId.trim())
    formData.append('file', file, file.name)
    const result = await runXhrUpload<UnzipUploadResponse>({
      url: `${PROXY_API_V1_URL}/ontology/documents/unzip`,
      formData,
      jobId,
      transferId,
      transferAliases: uploadId ? [jobId] : [],
      dispatch,
      signal,
      progress: {
        completedBytes,
        currentFileBytes: file.size,
        totalBytes
      },
      finalizeTransfer: finalRequest,
      ...(finalRequest && onUploadBytesComplete ? { onUploadBytesComplete } : {})
    })
    if (result.error) return { error: result.error }
    responses.push(result.data)
    completedBytes += file.size
  }

  const first = responses[0]
  const idempotentSkips = responses.flatMap((response) => response.idempotentSkips ?? [])
  const pathRenames = responses.flatMap((response) => response.pathRenames ?? [])
  return {
    data: {
      jobId: first.jobId,
      bucketPrefix: first.bucketPrefix,
      entriesUploaded: responses.reduce((sum, response) => sum + response.entriesUploaded, 0),
      entriesSkipped: responses.reduce((sum, response) => sum + response.entriesSkipped, 0),
      bytesUploaded: responses.reduce((sum, response) => sum + response.bytesUploaded, 0),
      entries: responses.flatMap((response) => response.entries),
      ...(idempotentSkips.length > 0 ? { idempotentSkips } : {}),
      ...(pathRenames.length > 0 ? { pathRenames } : {}),
      ...(responses.length === 1 && first.archiveRoot ? { archiveRoot: first.archiveRoot } : {}),
      ...(uploadId ? { uploadId } : {})
    }
  }
}

async function notifyUnstructuredUploadSent(jobId: string): Promise<void> {
  try {
    // Body-less POST: strip Content-Type so Fastify doesn't try to parse an empty JSON body.
    const headers = createOntologyHeaders()
    headers.delete('Content-Type')
    await fetch(
      `${ONTOLOGY_SERVICE_URL}/ingest/unstructured/jobs/${encodeURIComponent(jobId)}/upload-sent`,
      {
        method: 'POST',
        headers
      }
    )
  } catch {
    /* best-effort — job row may stay uploading until /process */
  }
}

async function notifyUnstructuredUploadFailed(jobId: string, error: string): Promise<void> {
  // Prefer the kind-agnostic route; unstructured alias remains for older servers.
  await notifyIngestUploadFailed(jobId, error)
}

async function notifyIngestUploadFailed(jobId: string, error: string): Promise<void> {
  try {
    await fetch(`${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/upload-failed`, {
      method: 'POST',
      headers: createOntologyHeaders(),
      body: JSON.stringify({ error })
    })
  } catch {
    /* best-effort — without this the row can stay stuck in uploading */
  }
}

/**
 * When an XHR upload fails before the server upload handler can mark the job
 * (401, network error, gateway timeout, …), report the failure so the job
 * leaves `uploading` immediately and the Jobs table / job logs show why.
 *
 * Returns only the error branch of `XhrUploadResult` so queryFn success
 * types stay non-optional (`data: T`, not `T | undefined`).
 */
export async function finalizeFailedIngestUpload(
  jobId: string,
  error: XhrUploadError,
  invalidate: () => void
): Promise<{ data?: undefined; error: XhrUploadError; meta?: undefined }> {
  const message = extractUploadErrorMessage(error)
  console.error(`[ingest] upload failed for ${jobId}: ${message}`, error)
  await notifyIngestUploadFailed(jobId, message)
  invalidate()
  return { error }
}

/**
 * Prefer `message` when the server returns AIP-style `{ error, message }`
 * (e.g. 401 UNAUTHENTICATED + Missing Authorization bearer token) so the
 * Jobs-table error string is actionable.
 */
export function extractUploadErrorMessage(error: XhrUploadError): string {
  if (error.status === 'FETCH_ERROR') {
    return error.error
  }
  const data = error.data
  if (typeof data === 'string' && data.trim()) return data
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>
    const code = record['error']
    const message = record['message']
    if (typeof message === 'string' && message.trim()) {
      if (typeof code === 'string' && code.trim() && code !== message) {
        return `${code}: ${message}`
      }
      return message
    }
    if (typeof code === 'string' && code.trim()) return code
  }
  return `Upload failed (${error.status})`
}

// ─── The slice ────────────────────────────────────────────────────

export const ingestApi = createApi({
  reducerPath: 'ingestApi',
  baseQuery: ontologyBaseQuery,
  refetchOnReconnect: true,
  tagTypes: INGEST_TAG_TYPES,
  endpoints: (builder) => ({
    getJobLogsPage: builder.query<JobLogPage, GetJobLogsPageArg>({
      query: ({ jobId, pageSize = 50, pageEnd, query }) => {
        const params = new URLSearchParams({ pageSize: String(pageSize) })
        if (pageEnd !== undefined) params.set('pageEnd', String(pageEnd))
        if (query) params.set('q', query)
        return `${ONTOLOGY_SERVICE_URL}/jobs/${encodeURIComponent(jobId)}/logs?${params.toString()}`
      },
      providesTags: (_result, _error, { jobId }) => [{ type: 'JobLogs', id: jobId }]
    }),
    listIngestIntakes: builder.query<ListIngestIntakesResponse, void>({
      query: () => `${PROXY_API_V1_URL}/ontology/ingest/intakes`,
      providesTags: [{ type: 'IngestIntakes', id: 'LIST' }]
    }),
    createIngestIntake: builder.mutation<IngestIntake, CreateIngestIntakeBody>({
      query: (body) => ({
        url: `${PROXY_API_V1_URL}/ontology/ingest/intakes`,
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'IngestIntakes', id: 'LIST' }]
    }),
    patchIngestIntake: builder.mutation<IngestIntake, { id: string; body: PatchIngestIntakeBody }>({
      query: ({ id, body }) => ({
        url: `${PROXY_API_V1_URL}/ontology/ingest/intakes/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [{ type: 'IngestIntakes', id: 'LIST' }]
    }),
    deleteIngestIntake: builder.mutation<void, { id: string; force?: boolean }>({
      query: ({ id, force }) => ({
        url: `${PROXY_API_V1_URL}/ontology/ingest/intakes/${encodeURIComponent(id)}${force ? '?force=true' : ''}`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: 'IngestIntakes', id: 'LIST' }]
    }),
    listIngestIntakeItems: builder.query<
      ListIngestIntakeItemsResponse,
      { id: string; limit?: number; before?: string }
    >({
      query: ({ id, limit = 50, before }) => {
        const params = new URLSearchParams({ limit: String(limit) })
        if (before) params.set('before', before)
        return `${PROXY_API_V1_URL}/ontology/ingest/intakes/${encodeURIComponent(id)}/items?${params}`
      },
      providesTags: (_result, _error, { id }) => [{ type: 'IngestIntakeItems', id }]
    }),
    /**
     * A file whose run would replace the rows of a different file under its
     * name answers 409 `FILE_NAME_EXISTS` until it is sent with `replaceExisting`.
     */
    runIngestIntakeItem: builder.mutation<
      { itemId: string; jobId: string },
      { itemId: string; force?: boolean; replaceExisting?: boolean }
    >({
      query: ({ itemId, force, replaceExisting }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/intake-items/${encodeURIComponent(itemId)}:run`,
        method: 'POST',
        ...(force || replaceExisting
          ? {
              body: {
                ...(force ? { force: true } : {}),
                ...(replaceExisting ? { replaceExisting: true } : {})
              }
            }
          : {})
      }),
      invalidatesTags: [
        { type: 'IngestIntakes', id: 'LIST' },
        { type: 'IngestIntakeItems' },
        { type: 'MappedTabularIngestJobs', id: 'LIST' }
      ]
    }),
    skipIngestIntakeItem: builder.mutation<IngestIntakeItem, { itemId: string }>({
      query: ({ itemId }) => ({
        url: `${PROXY_API_V1_URL}/ontology/ingest/intake-items/${encodeURIComponent(itemId)}:skip`,
        method: 'POST'
      }),
      invalidatesTags: [{ type: 'IngestIntakes', id: 'LIST' }, { type: 'IngestIntakeItems' }]
    }),
    /** Queues a held file, allowing it to replace the rows of the earlier file with its name. */
    confirmIngestIntakeItem: builder.mutation<IngestIntakeItem, { itemId: string }>({
      query: ({ itemId }) => ({
        url: `${PROXY_API_V1_URL}/ontology/ingest/intake-items/${encodeURIComponent(itemId)}:confirm`,
        method: 'POST'
      }),
      invalidatesTags: [{ type: 'IngestIntakes', id: 'LIST' }, { type: 'IngestIntakeItems' }]
    }),
    /** Earlier files of a tabular config named like one of `names`; a run of them replaces their rows. */
    listTabularFileNameMatches: builder.query<
      TabularFileNameMatch[],
      { configName: string; names: readonly string[] }
    >({
      query: ({ configName, names }) => ({
        url: `${PROXY_API_V1_URL}/ontology/ingest/tabular-file-names:match`,
        method: 'POST',
        body: { configName, files: names.map((name) => ({ name })) }
      }),
      transformResponse: (response: { matches: TabularFileNameMatch[] }) => response.matches
    }),
    // Create a structured ingest job server-side BEFORE the multi-hundred-MB
    // gwbk traverses the wire. The job lands in state="uploading" so the
    // Jobs table can show the row immediately; the paired `uploadStructuredFile`
    // mutation then streams the file at the per-job upload endpoint with a
    // live progress bar.
    beginStructuredJob: builder.mutation<
      { jobId: string; branchId?: string },
      {
        fileName: string
        siteId: string
        gatewayId: string
        fileType?: string
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/structured/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['StructuredJobs']
    }),
    uploadStructuredFile: builder.mutation<
      StructuredJob,
      {
        jobId: string
        file: File
        installPath?: string
        siteId: string
        gatewayId: string
        gatewayUrl?: string
        /**
         * Optional per-gateway Ignition WebDev project name. Persisted on the
         * Compute node and consumed by the proxy-server's live-data and
         * database paths instead of a global env var.
         */
        webDevProject?: string
        /**
         * Optional OPC UA credentials for the gateway's embedded OPC server
         * (`.gwbk` only). Stored per endpoint by the proxy and used by its
         * subscription manager instead of the proxy-wide env credentials.
         */
        opcUaUsername?: string
        opcUaPassword?: string
        /**
         * Optional Ignition WebDev Basic-auth for the HTTP gateway URL
         * (distinct from OPC UA). Stored per gateway by the proxy and used
         * on every outbound `/system/webdev/*` call.
         */
        webDevUsername?: string
        webDevPassword?: string
        gatewayLinkGatewayId?: string
        gatewayLinkCertificate?: string
        /** Requested mechanism for live Ignition tag reads. Defaults to WebDev. */
        tagRegistrationProtocol?: IgnitionTagRegistrationProtocol
        /**
         * `.gwbk` only. Explicit OPC UA endpoint URL (`opc.tcp://host:port`)
         * for the gateway's embedded OPC server. When set it overrides the
         * server-side host/port derivation used to register subscription tags.
         */
        opcUaEndpointUrl?: string
        /** `.gwbk` only. Acquisition behavior for existing and newly registered tags. */
        enableScraping?: boolean | 'preserve'
        /** `.gwbk` only. Insert state for new tags when acquisition is preserved. */
        newTagsEnabled?: boolean
        /** AVEVA `.bak` only; overrides AVEVA_SQL_* / AVEVA_BAK_HOST_DIR for this request. */
        avevaSqlPassword?: string
        avevaSqlUser?: string
        avevaSqlServer?: string
        avevaBakHostDir?: string
      }
    >({
      queryFn: async (
        {
          jobId,
          file,
          installPath,
          siteId,
          gatewayId,
          gatewayUrl,
          webDevProject,
          opcUaUsername,
          opcUaPassword,
          webDevUsername,
          webDevPassword,
          gatewayLinkGatewayId,
          gatewayLinkCertificate,

          tagRegistrationProtocol,
          opcUaEndpointUrl,
          enableScraping,
          newTagsEnabled,
          avevaSqlPassword,
          avevaSqlUser,
          avevaSqlServer,
          avevaBakHostDir
        },
        api
      ) => {
        const formData = createFormData({
          file,
          installPath,
          siteId,
          gatewayId,
          gatewayUrl,
          webDevProject,
          opcUaUsername,
          opcUaPassword,
          webDevUsername,
          webDevPassword,
          gatewayLinkGatewayId,
          gatewayLinkCertificate,

          tagRegistrationProtocol,
          opcUaEndpointUrl,
          enableScraping: enableScraping === undefined ? undefined : String(enableScraping),
          newTagsEnabled: newTagsEnabled === undefined ? undefined : String(newTagsEnabled),
          avevaSqlPassword,
          avevaSqlUser,
          avevaSqlServer,
          avevaBakHostDir
        })
        const result = await runXhrUpload<StructuredJob>({
          url: `${ONTOLOGY_SERVICE_URL}/ingest/structured/jobs/${encodeURIComponent(jobId)}/upload`,
          formData,
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (result.error) {
          return finalizeFailedIngestUpload(jobId, result.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['StructuredJobs']))
          )
        }
        return result
      },
      invalidatesTags: ['StructuredJobs']
    }),
    beginTagImportJob: builder.mutation<
      { jobId: string; branchId?: string },
      {
        fileName: string
        siteId: string
        gatewayId: string
        protocol: 'webdev' | 'opc'
        gatewayUrl?: string
        defaultOpcServer?: string
        defaultOpcEndpointUrl?: string
        defaultEnabled: boolean
        webdevProject?: string
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/tag-import/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TagImportJobs']
    }),
    uploadTagImportFile: builder.mutation<
      TagImportJob,
      {
        jobId: string
        file: File
        opcUaUsername?: string
        opcUaPassword?: string
      }
    >({
      queryFn: async ({ jobId, file, opcUaUsername, opcUaPassword }, api) => {
        const result = await runXhrUpload<TagImportJob>({
          url: `${ONTOLOGY_SERVICE_URL}/ingest/tag-import/jobs/${encodeURIComponent(jobId)}/upload`,
          formData: createFormData({ file, opcUaUsername, opcUaPassword }),
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (result.error) {
          return finalizeFailedIngestUpload(jobId, result.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['TagImportJobs']))
          )
        }
        return result
      },
      invalidatesTags: ['TagImportJobs']
    }),
    getTagImportJobDetail: builder.query<TagImportJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${encodeURIComponent(jobId)}`,
      providesTags: (_result, _error, jobId) => [{ type: 'TagImportJobs', id: jobId }]
    }),
    getTagImportErrorCsv: builder.query<string, string>({
      queryFn: async (jobId, api) => {
        const response = await fetch(
          `${ONTOLOGY_SERVICE_URL}/ingest/tag-import/jobs/${encodeURIComponent(jobId)}/errors.csv`,
          { headers: createOntologyHeaders(), signal: api.signal }
        )
        if (!response.ok) {
          return {
            error: {
              status: response.status,
              data: (await response.text().catch(() => '')) || response.statusText
            }
          }
        }
        return { data: await response.text() }
      }
    }),
    getTagImportTemplate: builder.query<string, 'webdev' | 'opc'>({
      queryFn: async (protocol, api) => {
        const response = await fetch(
          `${ONTOLOGY_SERVICE_URL}/ingest/tag-import/template.csv?protocol=${protocol}`,
          {
            headers: createOntologyHeaders(),
            signal: api.signal
          }
        )
        if (!response.ok) {
          return {
            error: {
              status: response.status,
              data: (await response.text().catch(() => '')) || response.statusText
            }
          }
        }
        return { data: await response.text() }
      }
    }),
    commitTagImportJob: builder.mutation<
      TagImportJob,
      { jobId: string; opcUaUsername?: string; opcUaPassword?: string }
    >({
      query: ({ jobId, opcUaUsername, opcUaPassword }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/tag-import/jobs/${encodeURIComponent(jobId)}/commit`,
        method: 'POST',
        body: { opcUaUsername, opcUaPassword }
      }),
      invalidatesTags: (_result, _error, { jobId }) => [{ type: 'TagImportJobs', id: jobId }]
    }),
    cancelTagImportJob: builder.mutation<TagImportJob, string>({
      query: (jobId) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/tag-import/jobs/${encodeURIComponent(jobId)}/cancel`,
        method: 'POST'
      }),
      invalidatesTags: (_result, _error, jobId) => [{ type: 'TagImportJobs', id: jobId }]
    }),
    beginTabularDumpJob: builder.mutation<
      { jobId: string; branchId?: string },
      {
        fileName: string
        siteId: string
        connectionName: string
        replaceExisting?: boolean
        tableNames?: string[]
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/tabular-dump/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TabularDumpJobs']
    }),
    previewTabularDump: builder.mutation<
      { tables: TabularDumpTablePreview[]; issues: TabularDumpIssue[] },
      { file: File; tableNames?: string[] }
    >({
      queryFn: async ({ file, tableNames }, api) => {
        const formData = createFormData({
          file,
          tableNames: tableNames && tableNames.length > 0 ? JSON.stringify(tableNames) : undefined
        })
        const headers = createOntologyHeaders()
        headers.delete('Content-Type')
        try {
          const response = await fetch(`${ONTOLOGY_SERVICE_URL}/ingest/tabular-dump/preview`, {
            method: 'POST',
            headers,
            body: formData,
            signal: api.signal
          })
          const json = (await response.json().catch(() => ({}))) as {
            tables?: TabularDumpTablePreview[]
            issues?: TabularDumpIssue[]
            error?: string
            message?: string
          }
          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: json.error ?? json.message ?? 'dump preview failed'
              }
            }
          }
          return {
            data: { tables: json.tables ?? [], issues: json.issues ?? [] }
          }
        } catch (error) {
          if (api.signal.aborted) {
            return { error: { status: 'CUSTOM_ERROR', error: 'Aborted' } }
          }
          const message = error instanceof Error ? error.message : 'dump preview failed'
          return { error: { status: 'FETCH_ERROR', error: message } }
        }
      }
    }),
    uploadTabularDumpFile: builder.mutation<TabularDumpJob, { jobId: string; file: File }>({
      queryFn: async ({ jobId, file }, api) => {
        const result = await runXhrUpload<TabularDumpJob>({
          url: `${ONTOLOGY_SERVICE_URL}/ingest/tabular-dump/jobs/${encodeURIComponent(jobId)}/upload`,
          formData: createFormData({ file }),
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (result.error) {
          return finalizeFailedIngestUpload(jobId, result.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['TabularDumpJobs']))
          )
        }
        return result
      },
      invalidatesTags: ['TabularDumpJobs']
    }),
    getTabularDumpJobDetail: builder.query<TabularDumpJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${encodeURIComponent(jobId)}`,
      providesTags: (_result, _error, jobId) => [{ type: 'TabularDumpJobs', id: jobId }]
    }),
    connectSql: builder.mutation<ConnectSqlResult, ConnectSqlInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/sql/connect`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['SqlJobs']
    }),
    getSqlJobDetail: builder.query<SqlJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${encodeURIComponent(jobId)}`,
      providesTags: (_result, _error, jobId) => [{ type: 'SqlJobs', id: jobId }]
    }),
    previewSoap: builder.mutation<SoapContractPreview, PreviewSoapInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/soap/preview`,
        method: 'POST',
        body
      })
    }),
    connectSoap: builder.mutation<ConnectSoapResult, ConnectSoapInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/soap/connect`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['SoapJobs']
    }),
    getSoapJobDetail: builder.query<SoapJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${encodeURIComponent(jobId)}`,
      providesTags: (_result, _error, jobId) => [{ type: 'SoapJobs', id: jobId }]
    }),
    discoverPss: builder.mutation<PssDiscoverResponse, DiscoverPssInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/pss/discover`,
        method: 'POST',
        body
      })
    }),
    getPssJobDetail: builder.query<PssJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${encodeURIComponent(jobId)}`,
      providesTags: (_result, _error, jobId) => [{ type: 'PssJobs', id: jobId }]
    }),
    beginAvevaGraphicsJob: builder.mutation<
      { jobId: string; branchId?: string },
      { fileName: string; siteId: string; gatewayId: string }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/aveva-graphics/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['StructuredJobs']
    }),
    uploadAvevaGraphicsFile: builder.mutation<
      StructuredJob,
      {
        jobId: string
        file: File
        siteId: string
        gatewayId: string
      }
    >({
      queryFn: async ({ jobId, file, siteId, gatewayId }, api) => {
        const formData = createFormData({ file, siteId, gatewayId })
        const result = await runXhrUpload<StructuredJob>({
          url: `${ONTOLOGY_SERVICE_URL}/ingest/aveva-graphics/jobs/${encodeURIComponent(jobId)}/upload`,
          formData,
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (result.error) {
          return finalizeFailedIngestUpload(jobId, result.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['StructuredJobs']))
          )
        }
        return result
      },
      invalidatesTags: ['StructuredJobs']
    }),
    getStructuredJobs: builder.query<StructuredJob[], void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/jobs?kind=structured`,
      providesTags: ['StructuredJobs']
    }),
    getStructuredJobDetail: builder.query<StructuredJobDetail, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'StructuredJobs', id: jobId }]
    }),
    // Lazy lookup of the full diff operations for a diff-pending-apply job.
    // The api-service serves this from its in-memory diff cache; expired or
    // missing entries 404. Loaded on demand by the JobDetailPanel.
    getJobDiffPreview: builder.query<DiffPreviewResponse, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${encodeURIComponent(jobId)}/diff-preview`,
      providesTags: (_result, _error, jobId) => [{ type: 'StructuredJobs', id: jobId }]
    }),
    checkExistingGateway: builder.query<
      CheckExistingResponse,
      { siteId: string; gatewayId: string }
    >({
      query: ({ siteId, gatewayId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/structured/check-existing`,
        params: { siteId, gatewayId }
      }),
      providesTags: ['StructuredJobs']
    }),
    checkStorageStatus: builder.query<StorageStatusResponse, void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/ingest/storage-status`
    }),
    /**
     * Site-scoped inventory under `site-ingest/{org}/{siteId}/…`.
     * Pass `kind` (`project-roots` | `plc` | `documents` | `ip-map`) to list one
     * family reliably — the response `kind` field is a leaf path segment
     * and mis-labels nested SCADA/PLC keys.
     */
    listSiteDocuments: builder.query<
      SiteDocument[],
      {
        siteId: string
        kind?: SiteDocumentKind
        /** Extra site_id values to merge (URN ↔ slug). */ alsoSiteIds?: string[]
      }
    >({
      async queryFn({ siteId, kind, alsoSiteIds }, _api, _extra, baseQuery) {
        const ids = [siteId, ...(alsoSiteIds ?? [])].map((id) => id.trim()).filter(Boolean)
        const uniqueIds = [...new Set(ids)]
        const byKey = new Map<string, SiteDocument>()
        let lastError: unknown
        let anySuccess = false
        for (const id of uniqueIds) {
          const result = await baseQuery({
            url: `${PROXY_API_V1_URL}/ontology/documents`,
            params: {
              site_id: id,
              ...(kind ? { kind } : {})
            }
          })
          if (result.error) {
            // One bad/missing prefix should not hide docs found under an alternate id.
            lastError = result.error
            continue
          }
          anySuccess = true
          const docs = (result.data as { documents?: SiteDocument[] } | undefined)?.documents ?? []
          for (const doc of docs) {
            const key =
              doc.key?.trim() || `${doc.fileName}\0${doc.kind}\0${doc.lastModified}\0${doc.size}`
            if (!byKey.has(key)) byKey.set(key, doc)
          }
        }
        if (!anySuccess && lastError) {
          return { error: lastError as { status: number; data: unknown } }
        }
        return { data: [...byKey.values()] }
      },
      providesTags: (_result, _error, { siteId }) => [{ type: 'SiteDocuments', id: siteId }]
    }),
    /**
     * Unstructured corpus files for a site.
     *
     * Primary source: proxy `GET /documents/unzips?site_id=` (S3 prefixes
     * bound via `.axilon/site.json`) + per-job file listing — this survives
     * missing jobs / ingest_job_files rows. Non-unstructured job kinds
     * (e.g. ip_mapping) that also wrote a site sidecar are filtered out.
     * Fallback: unstructured jobs matching siteId, then job-files when present
     * (richer status), else the same S3 file list.
     */
    listSiteUnstructuredFiles: builder.query<
      SiteUnstructuredFile[],
      {
        siteId: string
        /** URN ↔ slug aliases for job.siteId matching. */ alsoSiteIds?: string[]
      }
    >({
      async queryFn({ siteId, alsoSiteIds }, _api, _extra, baseQuery) {
        const siteIds = [
          ...new Set([siteId, ...(alsoSiteIds ?? [])].map((id) => id.trim()).filter(Boolean))
        ]
        if (siteIds.length === 0) return { data: [] }

        const jobIds = new Set<string>()

        for (const id of siteIds) {
          const unzipJobs = await baseQuery({
            url: `${PROXY_API_V1_URL}/ontology/documents/unzips`,
            params: { site_id: id }
          })
          if (unzipJobs.error) continue
          const jobs =
            (unzipJobs.data as { jobs?: Array<{ jobId?: string }> } | undefined)?.jobs ?? []
          for (const job of jobs) {
            const jid = job.jobId?.trim()
            if (jid) jobIds.add(jid)
          }
        }

        // /documents/unzips is kind-agnostic (any corpus with .axilon/site.json).
        // Drop IP-mapping / robotics / other non-unstructured jobs so those
        // files only appear under their dedicated Ingested files groups.
        for (const jid of [...jobIds]) {
          const jobResult = await baseQuery({
            url: `${PROXY_API_V1_URL}/ontology/jobs/${encodeURIComponent(jid)}`
          })
          if (jobResult.error) {
            // 404 / missing row: keep — treat as an orphaned unstructured corpus.
            continue
          }
          const kind = String((jobResult.data as { kind?: string } | undefined)?.kind ?? '').trim()
          if (kind && kind !== 'unstructured') {
            jobIds.delete(jid)
          }
        }

        for (let page = 0; page < SITE_UNSTRUCTURED_JOB_MAX_PAGES; page++) {
          const pageResult = await baseQuery({
            url: `${ONTOLOGY_SERVICE_URL}/jobs-page`,
            params: {
              kind: 'unstructured',
              page: String(page),
              pageSize: String(SITE_UNSTRUCTURED_JOB_PAGE_SIZE)
            }
          })
          if (pageResult.error) break
          const payload = (pageResult.data ?? {}) as Partial<JobsPage>
          const jobs = Array.isArray(payload.jobs) ? payload.jobs : []
          for (const job of jobs) {
            if (job.kind !== 'unstructured') continue
            const jobSite = job.siteId?.trim()
            if (!jobSite || !siteIds.includes(jobSite)) continue
            jobIds.add(job.id)
            if (jobIds.size >= SITE_UNSTRUCTURED_JOB_CAP) break
          }
          if (jobIds.size >= SITE_UNSTRUCTURED_JOB_CAP || !payload.hasNext) break
        }

        const byKey = new Map<string, SiteUnstructuredFile>()
        for (const jobId of jobIds) {
          let listedFromInventory = false
          const filesResult = await baseQuery({
            url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/job-files`
          })
          if (!filesResult.error) {
            const files = (filesResult.data as { files?: IngestJobFile[] } | undefined)?.files ?? []
            for (const file of files) {
              const path = file.path?.trim()
              if (!path || isUnstructuredCorpusNoisePath(path)) continue
              const fileName = basenamePath(path)
              if (!fileName || fileName === '.' || fileName === '..') continue
              const key = `${jobId}\0${path}`
              if (byKey.has(key)) continue
              byKey.set(key, {
                jobId,
                path,
                fileName,
                status: file.status || 'pending',
                updatedAtMs: typeof file.updatedAt === 'number' ? file.updatedAt : 0
              })
              listedFromInventory = true
            }
          }

          if (listedFromInventory) continue

          const s3Files = await baseQuery({
            url: `${PROXY_API_V1_URL}/ontology/documents/unzips/${encodeURIComponent(jobId)}`
          })
          if (s3Files.error) continue
          const files =
            (
              s3Files.data as
                | {
                    files?: Array<{
                      path?: string
                      fileName?: string
                      lastModified?: string
                    }>
                  }
                | undefined
            )?.files ?? []
          for (const file of files) {
            const path = file.path?.trim()
            if (!path || isUnstructuredCorpusNoisePath(path)) continue
            const fileName = (file.fileName || basenamePath(path)).trim()
            if (!fileName || fileName === '.' || fileName === '..') continue
            const key = `${jobId}\0${path}`
            if (byKey.has(key)) continue
            const updatedAtMs = file.lastModified ? Date.parse(file.lastModified) : 0
            byKey.set(key, {
              jobId,
              path,
              fileName,
              status: 'stored',
              updatedAtMs: Number.isFinite(updatedAtMs) ? updatedAtMs : 0
            })
          }
        }

        return {
          data: [...byKey.values()].sort((a, b) => {
            if (b.updatedAtMs !== a.updatedAtMs) return b.updatedAtMs - a.updatedAtMs
            return a.fileName.localeCompare(b.fileName)
          })
        }
      },
      providesTags: (_result, _error, { siteId }) => [
        { type: 'SiteDocuments', id: siteId },
        'UnstructuredJobs'
      ]
    }),
    deleteIngestedFile: builder.mutation<void, DeleteIngestedFileArg>({
      query: (arg) => ({
        url: `${PROXY_API_V1_URL}/ontology/documents`,
        method: 'DELETE',
        params: 'key' in arg ? { key: arg.key } : { job_id: arg.jobId, path: arg.path },
        responseHandler: 'text'
      }),
      invalidatesTags: (_result, _error, arg) =>
        'jobId' in arg
          ? ['SiteDocuments', 'UnstructuredJobs', { type: 'UnstructuredJobs', id: arg.jobId }]
          : ['SiteDocuments', 'UnstructuredJobs']
    }),
    beginStructuredDiffJob: builder.mutation<
      { jobId: string; branchId?: string },
      {
        fileName: string
        siteId: string
        gatewayId: string
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest-diff/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['StructuredJobs']
    }),
    previewStructuredDiff: builder.mutation<
      // The endpoint queues the diff preview as a background job and returns
      // the new job's id immediately. The client polls /api/ontology/jobs/:id
      // for state transitions and log entries; the diff result lands on the
      // job (and the cached diff is reachable via the job's `diffId`).
      { jobId: string },
      {
        jobId: string
        file: File
        siteId: string
        gatewayId: string
        installPath?: string
        gatewayUrl?: string
        /**
         * Optional per-gateway Ignition WebDev project name. Forwarded into
         * the diff cache so a subsequent diff/apply persists it on the
         * Compute node, matching what a full ingest would do.
         */
        webDevProject?: string
        /** Optional OPC UA credentials; used if preview falls back to full ingest. */
        opcUaUsername?: string
        opcUaPassword?: string
        /** Optional Ignition WebDev Basic-auth; used on full-ingest fallback. */
        webDevUsername?: string
        webDevPassword?: string
        gatewayLinkGatewayId?: string
        gatewayLinkCertificate?: string
        /** Requested mechanism used if the diff is applied or falls back to full ingest. */
        tagRegistrationProtocol?: IgnitionTagRegistrationProtocol
        /** Explicit OPC UA endpoint; used on full-ingest fallback. */
        opcUaEndpointUrl?: string
        /** Acquisition behavior used if the diff is applied or falls back to full ingest. */
        enableScraping?: boolean | 'preserve'
        /** Insert state for new tags when acquisition is preserved. */
        newTagsEnabled?: boolean
        /**
         * Fail instead of handing a missing or corrupt stored baseline to
         * full ingest. A gateway with no project-root version still falls
         * back to a preservation-mode full ingest.
         */
        strictDiff?: boolean
        /** Re-run UDT mappers even when the ProjectRoot source is unchanged. */
        remapUnchangedUdts?: boolean
      }
    >({
      queryFn: async (
        {
          jobId,
          file,
          siteId,
          gatewayId,
          installPath,
          gatewayUrl,
          webDevProject,
          opcUaUsername,
          opcUaPassword,
          webDevUsername,
          webDevPassword,
          gatewayLinkGatewayId,
          gatewayLinkCertificate,

          tagRegistrationProtocol,
          opcUaEndpointUrl,
          enableScraping,
          newTagsEnabled,
          strictDiff,
          remapUnchangedUdts
        },
        api
      ) => {
        const formData = createFormData({
          file,
          siteId,
          gatewayId,
          installPath,
          gatewayUrl,
          webDevProject,
          opcUaUsername,
          opcUaPassword,
          webDevUsername,
          webDevPassword,
          gatewayLinkGatewayId,
          gatewayLinkCertificate,

          tagRegistrationProtocol,
          opcUaEndpointUrl,
          enableScraping: enableScraping === undefined ? undefined : String(enableScraping),
          newTagsEnabled: newTagsEnabled === undefined ? undefined : String(newTagsEnabled),
          strictDiff: strictDiff === undefined ? undefined : String(strictDiff),
          remapUnchangedUdts:
            remapUnchangedUdts === undefined ? undefined : String(remapUnchangedUdts),
          mode: 'preview'
        })
        const result = await runXhrUpload<{ jobId: string }>({
          url: `${ONTOLOGY_SERVICE_URL}/ingest-diff/jobs/${encodeURIComponent(jobId)}/upload`,
          formData,
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (result.error) {
          return finalizeFailedIngestUpload(jobId, result.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['StructuredJobs']))
          )
        }
        return result
      },
      invalidatesTags: ['StructuredJobs']
    }),
    applyStructuredDiff: builder.mutation<
      DiffUploadResponse,
      { diffId: string; siteId: string; gatewayId: string; jobId?: string }
    >({
      query: ({ diffId, siteId, gatewayId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/diff/apply-structured`,
        method: 'POST',
        body: { diffId, siteId, gatewayId }
      }),
      // Graph-derived caches are invalidated by the shared coherence bridge.
      invalidatesTags: ['StructuredJobs'],
      // Optimistically flip the affected job's state to 'processing' the
      // moment the user clicks Apply, so the row's status pill stops
      // reading "review" immediately. Without this, the pill stays stuck
      // until the apply mutation resolves (several seconds) because the
      // list query won't re-poll until it sees an active job in its data.
      async onQueryStarted({ jobId }, { dispatch, queryFulfilled }) {
        if (!jobId) {
          try {
            await queryFulfilled
          } catch {
            // Mutation failure is surfaced by the caller via the unwrap
            // promise; nothing to do here when there's no optimistic patch
            // to roll back.
          }
          return
        }
        const listPatch = dispatch(
          ingestApi.util.updateQueryData('getStructuredJobs', undefined, (draft) => {
            const job = draft.find((j) => j.id === jobId)
            if (job) job.state = 'processing'
          })
        )
        const detailPatch = dispatch(
          ingestApi.util.updateQueryData('getStructuredJobDetail', jobId, (draft) => {
            if (draft) draft.state = 'processing'
          })
        )
        try {
          await queryFulfilled
        } catch {
          listPatch.undo()
          detailPatch.undo()
        }
      }
    }),
    getIngestJobsPage: builder.query<
      JobsPage,
      {
        kind?:
          | 'all'
          | 'structured'
          | 'plc'
          | 'robotics'
          | 'vision'
          | 'mtconnect'
          | 'pi'
          | 'canary'
          | 'experion'
          | 'srs'
          | 'safety_manager'
          | 'tag_mapping'
          | 'unstructured'
          | 'ip_mapping'
          | 'tag_import'
          | 'tabular_dump'
          | 'mapped_tabular_ingest'
          | 'mapped_tabular_ingest_build'
          | 'sql'
          | 'soap'
          | 'work_orders'
        page?: number
        pageSize?: number
        branchId?: string
        associatedBranchId?: string
        /** Drop runs started from this source; runs without a source are kept. */
        excludeSourceKind?: 'intake'
      }
    >({
      query: ({
        kind = 'all',
        page = 0,
        pageSize = 30,
        branchId,
        associatedBranchId,
        excludeSourceKind
      }) => {
        const params = new URLSearchParams({
          kind,
          page: String(page),
          pageSize: String(pageSize)
        })
        if (branchId) params.set('branchId', branchId)
        if (associatedBranchId) params.set('associatedBranchId', associatedBranchId)
        if (excludeSourceKind) params.set('excludeSourceKind', excludeSourceKind)
        return `${ONTOLOGY_SERVICE_URL}/jobs-page?${params.toString()}`
      },
      providesTags: [
        'StructuredJobs',
        'PlcJobs',
        'RoboticsJobs',
        'VisionJobs',
        'MTConnectJobs',
        'PIJobs',
        'CanaryJobs',
        'ExperionJobs',
        'SrsJobs',
        'SafetyManagerJobs',
        'TagMappingJobs',
        'UnstructuredJobs',
        'IpMappingJobs',
        'TagImportJobs',
        'TabularDumpJobs',
        'MappedTabularIngestJobs',
        'SqlJobs',
        'SoapJobs',
        'PssJobs',
        'WorkOrdersJobs'
      ]
    }),
    renameIngestRun: builder.mutation<IngestRun, { runId: string; name: string }>({
      query: ({ runId, name }) => ({
        url: `${PROXY_API_V1_URL}/ontology/ingest-runs/${encodeURIComponent(runId)}`,
        method: 'PATCH',
        body: { name }
      }),
      invalidatesTags: [
        'StructuredJobs',
        'PlcJobs',
        'RoboticsJobs',
        'VisionJobs',
        'MTConnectJobs',
        'PIJobs',
        'CanaryJobs',
        'ExperionJobs',
        'SrsJobs',
        'SafetyManagerJobs',
        'TagMappingJobs',
        'UnstructuredJobs',
        'IpMappingJobs',
        'TagImportJobs',
        'TabularDumpJobs',
        'MappedTabularIngestJobs',
        'SqlJobs',
        'SoapJobs',
        'PssJobs',
        'WorkOrdersJobs'
      ]
    }),
    beginPlcJob: builder.mutation<
      { jobId: string; branchId?: string },
      {
        fileName: string
        siteId: string
        controllerId?: string
        vendor?: string
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/plc/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['PlcJobs']
    }),
    uploadPlcFile: builder.mutation<
      PlcJob | PlcJob[],
      {
        jobId: string
        file: File
        siteId: string
        controllerId?: string
        vendor?: string
      }
    >({
      queryFn: async ({ jobId, file, siteId, controllerId, vendor }, api) => {
        const formData = createFormData({ file, siteId, controllerId, vendor })
        const result = await runXhrUpload<PlcJob | PlcJob[]>({
          url: `${ONTOLOGY_SERVICE_URL}/ingest/plc/jobs/${encodeURIComponent(jobId)}/upload`,
          formData,
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (result.error) {
          return finalizeFailedIngestUpload(jobId, result.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['PlcJobs']))
          )
        }
        return result
      },
      invalidatesTags: ['PlcJobs']
    }),
    getPlcJobs: builder.query<PlcJob[], void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/jobs?kind=plc`,
      providesTags: ['PlcJobs']
    }),
    getPlcJobDetail: builder.query<PlcJobDetail, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'PlcJobs', id: jobId }]
    }),
    beginRoboticsJob: builder.mutation<
      { jobId: string; branchId?: string },
      {
        fileName: string
        siteId: string
        controllerId: string
        equipmentId?: string
        vendor?: string
        allowIdentityOverwrite?: boolean
        persistDeterministicJoins?: boolean
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/robotics/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['RoboticsJobs']
    }),
    uploadRoboticsFile: builder.mutation<
      PlcJob,
      {
        jobId: string
        file: File
        siteId: string
        controllerId: string
        equipmentId?: string
        vendor?: string
        aiInference?: boolean
        allowIdentityOverwrite?: boolean
        persistDeterministicJoins?: boolean
      }
    >({
      queryFn: async (
        {
          jobId,
          file,
          siteId,
          controllerId,
          equipmentId,
          vendor,
          aiInference,
          allowIdentityOverwrite,
          persistDeterministicJoins
        },
        api
      ) => {
        const formData = createFormData({
          file,
          siteId,
          controllerId,
          equipmentId,
          vendor,
          // Opt-in AI inference post-pass; omitted => deterministic-only ingest.
          ...(aiInference ? { aiInference: 'true' } : {}),
          ...(allowIdentityOverwrite ? { allowIdentityOverwrite: 'true' } : {}),
          ...(persistDeterministicJoins ? { persistDeterministicJoins: 'true' } : {})
        })
        const result = await runXhrUpload<PlcJob>({
          url: `${ONTOLOGY_SERVICE_URL}/ingest/robotics/jobs/${encodeURIComponent(jobId)}/upload`,
          formData,
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (result.error) {
          return finalizeFailedIngestUpload(jobId, result.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['RoboticsJobs']))
          )
        }
        return result
      },
      invalidatesTags: ['RoboticsJobs']
    }),
    getRoboticsJobs: builder.query<PlcJob[], void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/jobs?kind=robotics`,
      providesTags: ['RoboticsJobs']
    }),
    getRoboticsJobDetail: builder.query<PlcJobDetail, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'RoboticsJobs', id: jobId }]
    }),
    // Cognex vision backups: one upload is one `vision` job that writes the
    // cameras and their jobs. PLC links are the separate `VISION_PLC` mapping.
    beginVisionJob: builder.mutation<
      { jobId: string; branchId?: string },
      {
        fileName: string
        siteId: string
        deviceName?: string
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/vision/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['VisionJobs']
    }),
    uploadVisionFile: builder.mutation<
      { jobId: string; branchId?: string },
      {
        jobId: string
        file: File
        siteId: string
        deviceName?: string
        /** Pairing hints for a single-camera upload, used by the mapping job. */
        plcModuleName?: string
        ipAddress?: string
      }
    >({
      queryFn: async ({ jobId, file, siteId, deviceName, plcModuleName, ipAddress }, api) => {
        const formData = createFormData({
          file,
          siteId,
          deviceName,
          plcModuleName,
          ipAddress
        })
        const result = await runXhrUpload<{ jobId: string; branchId?: string }>({
          url: `${ONTOLOGY_SERVICE_URL}/ingest/vision/jobs/${encodeURIComponent(jobId)}/upload`,
          formData,
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (result.error) {
          return finalizeFailedIngestUpload(jobId, result.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['VisionJobs']))
          )
        }
        return result
      },
      invalidatesTags: ['VisionJobs']
    }),
    getVisionJobDetail: builder.query<PlcJobDetail, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'VisionJobs', id: jobId }]
    }),
    connectMTConnectAgent: builder.mutation<
      ConnectMTConnectAgentResult,
      ConnectMTConnectAgentInput
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/mtconnect/connect`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['MTConnectJobs']
    }),
    getMTConnectJobDetail: builder.query<MTConnectJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'MTConnectJobs', id: jobId }]
    }),
    connectPI: builder.mutation<ConnectPIResult, ConnectPIInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/pi/connect`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['PIJobs']
    }),
    suggestPIHistoryMapping: builder.mutation<
      TagMappingSuggestResult & {
        probe: { dataServers: number; points: number; existingTags: number }
      },
      PIHistoryMappingInput
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/pi/suggest`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TagMappingJobs']
    }),
    previewPIHistoryMapping: builder.mutation<TagMappingPreviewResult, PIHistoryMappingInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/pi/preview`,
        method: 'POST',
        body
      })
    }),
    getPIJobDetail: builder.query<PIJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'PIJobs', id: jobId }]
    }),
    connectCanary: builder.mutation<ConnectCanaryResult, ConnectCanaryInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/canary/connect`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['CanaryJobs']
    }),
    suggestCanaryHistoryMapping: builder.mutation<
      TagMappingSuggestResult & {
        probe: { nodes: number; tags: number; existingTags: number }
      },
      CanaryHistoryMappingInput
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/canary/suggest`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TagMappingJobs']
    }),
    previewCanaryHistoryMapping: builder.mutation<
      TagMappingPreviewResult,
      CanaryHistoryMappingInput
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/canary/preview`,
        method: 'POST',
        body
      })
    }),
    getCanaryJobDetail: builder.query<CanaryJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'CanaryJobs', id: jobId }]
    }),
    connectExperionErdb: builder.mutation<ConnectExperionErdbResult, ConnectExperionErdbInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/experion/connect`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['ExperionJobs']
    }),
    getExperionJobDetail: builder.query<ExperionJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'ExperionJobs', id: jobId }]
    }),
    getSrsJobDetail: builder.query<SrsJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'SrsJobs', id: jobId }]
    }),
    listTagMappingTypes: builder.query<TagMappingTypeSummary[], void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/ingest/tag-mapping/types`
    }),
    previewTagMapping: builder.mutation<
      TagMappingPreviewResult,
      {
        mappingTypeId: string
        aGrammar?: string
        bGrammar?: string
        enumMaps?: string
        enums?: TagMappingEnumJson
        grammars?: Array<{
          id?: string
          label?: string
          rationale?: string
          a?: Array<{ name: string; type: string; regex: string }>
          b?: Array<{ name: string; type: string; regex: string }>
          enums?: TagMappingEnumJson
          join?: string[]
          aGrammar?: string
          bGrammar?: string
          enumMaps?: string
          exclude?: TagMappingLinkExclude[]
        }>
        manualLinks?: TagMappingLinkExclude[]
        aSamples?: string[]
        bSamples?: string[]
        siteId?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/tag-mapping/preview`,
        method: 'POST',
        body
      })
    }),
    suggestTagMapping: builder.mutation<
      TagMappingSuggestResult,
      {
        mappingTypeId: string
        siteId: string
        additionalContext?: string
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/tag-mapping/suggest`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TagMappingJobs']
    }),
    runTagMapping: builder.mutation<
      { jobId: string; branchId?: string },
      {
        mappingTypeId: string
        siteId: string
        aGrammar?: string
        bGrammar?: string
        enumMaps?: string
        enums?: TagMappingEnumJson
        grammars?: Array<{
          id?: string
          label?: string
          rationale?: string
          a?: Array<{ name: string; type: string; regex: string }>
          b?: Array<{ name: string; type: string; regex: string }>
          enums?: TagMappingEnumJson
          join?: string[]
          aGrammar?: string
          bGrammar?: string
          enumMaps?: string
          exclude?: TagMappingLinkExclude[]
        }>
        manualLinks?: TagMappingLinkExclude[]
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/tag-mapping/run`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TagMappingJobs']
    }),
    getTagMappingJobDetail: builder.query<TagMappingJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'TagMappingJobs', id: jobId }]
    }),
    beginSrsJob: builder.mutation<
      { jobId: string; branchId?: string },
      {
        fileName: string
        siteId?: string
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/srs/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['SrsJobs']
    }),
    uploadSrsFile: builder.mutation<
      SrsJob,
      {
        jobId: string
        file: File
        siteId?: string
        siteName?: string
        password?: string
      }
    >({
      queryFn: async ({ jobId, file, siteId, siteName, password }, api) => {
        const formData = createFormData({ file, siteId, siteName, password })
        const result = await runXhrUpload<SrsJob>({
          url: `${ONTOLOGY_SERVICE_URL}/ingest/srs/jobs/${encodeURIComponent(jobId)}/upload`,
          formData,
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (result.error) {
          return finalizeFailedIngestUpload(jobId, result.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['SrsJobs']))
          )
        }
        return result
      },
      invalidatesTags: ['SrsJobs']
    }),
    getSafetyManagerJobDetail: builder.query<SafetyManagerJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'SafetyManagerJobs', id: jobId }]
    }),
    beginSafetyManagerJob: builder.mutation<
      { jobId: string; branchId?: string },
      {
        fileName: string
        siteId?: string
        systemId?: string
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/safety-manager/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['SafetyManagerJobs']
    }),
    connectSafetyManagerDb: builder.mutation<
      ConnectSafetyManagerDbResult,
      ConnectSafetyManagerDbInput
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/safety-manager/connect`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['SafetyManagerJobs']
    }),
    uploadSafetyManagerFile: builder.mutation<
      SafetyManagerJob,
      {
        jobId: string
        file: File
        siteId?: string
        siteName?: string
        systemId?: string
      }
    >({
      queryFn: async ({ jobId, file, siteId, siteName, systemId }, api) => {
        const formData = createFormData({ file, siteId, siteName, systemId })
        const result = await runXhrUpload<SafetyManagerJob>({
          url: `${ONTOLOGY_SERVICE_URL}/ingest/safety-manager/jobs/${encodeURIComponent(jobId)}/upload`,
          formData,
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (result.error) {
          return finalizeFailedIngestUpload(jobId, result.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['SafetyManagerJobs']))
          )
        }
        return result
      },
      invalidatesTags: ['SafetyManagerJobs']
    }),
    // Create a zip ingest job server-side BEFORE the archive traverses the
    // wire. The job lands in state="uploading" so the Jobs table can show
    // the row immediately; the paired `uploadUnstructuredFiles` mutation then streams
    // the file directly to the proxy unzip endpoint and calls `/process` to
    // kick off classification.
    beginUnstructuredJob: builder.mutation<
      { jobId: string; branchId?: string },
      { fileName: string }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/unstructured/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['UnstructuredJobs']
    }),
    /**
     * Resolve append | reopen | fresh and claim an open uploadId. Does not
     * flip terminal job state — that happens at complete.
     */
    beginDocumentAddition: builder.mutation<
      BeginDocumentAdditionResult,
      {
        fileName: string
        contextJobId?: string
        uploadId?: string
        siteId?: string
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/unstructured/document-additions/begin`,
        method: 'POST',
        body
      })
    }),
    /**
     * Browser → proxy raw unzip for a corpus batch. Progress/abort are keyed
     * by `uploadId` when provided (document addition), else `jobId` (fresh
     * Ingest). Does not call `/process` or document-additions complete.
     */
    uploadUnstructuredCorpus: builder.mutation<
      UnzipUploadResponse,
      { jobId: string; files: File[]; uploadId?: string; siteId?: string }
    >({
      queryFn: async ({ jobId, files, uploadId, siteId }, api) => {
        if (files.length === 0) {
          return {
            error: { status: 'FETCH_ERROR', error: 'No files to upload' }
          }
        }
        return uploadCorpusFilesSequentially({
          jobId,
          files,
          uploadId,
          siteId,
          dispatch: api.dispatch,
          signal: api.signal
        })
      }
    }),
    /**
     * Land a successful corpus unzip: clear the busy claim, record the Lead
     * message, and wake/reopen as needed. Fresh-mode jobs use the same route
     * (server routes first landing through finishUnstructured).
     */
    completeDocumentAddition: builder.mutation<
      CompleteDocumentAdditionResult,
      {
        jobId: string
        uploadId: string
        unzip: UnzipUploadResponse
        fileName?: string
        /** Optional operator guidance for the Lead (fresh job or addition). */
        additionalContext?: string
      }
    >({
      query: ({ jobId, uploadId, unzip, fileName, additionalContext }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/unstructured/jobs/${encodeURIComponent(jobId)}/document-additions/${encodeURIComponent(uploadId)}/complete`,
        method: 'POST',
        body: {
          unzip,
          ...(fileName ? { fileName } : {}),
          ...(additionalContext ? { additionalContext } : {})
        }
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        'UnstructuredJobs',
        { type: 'UnstructuredJobs', id: jobId },
        { type: 'IngestQuestions', id: jobId }
      ]
    }),
    abortDocumentAddition: builder.mutation<void, { jobId: string; uploadId: string }>({
      query: ({ jobId, uploadId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/unstructured/jobs/${encodeURIComponent(jobId)}/document-additions/${encodeURIComponent(uploadId)}/abort`,
        method: 'POST'
      })
    }),
    // Two-step structured-document mapping ingest. The `documentType`
    // selects which pipeline runs; both share the begin + upload
    // lifecycle and the `ip_mapping` audit-row job kind:
    //   - `IP`           → /ingest/ip-mapping/*           (Compute → Controller)
    //   - `ROBOTICS_PLC` → /ingest/robotics-plc-mapping/* (PLCTag → RobotIOSignal)
    //   1. POST <base>/begin            → creates a job in state="uploading"
    //   2. POST <base>/jobs/:jobId/upload → multipart, kicks off the async
    //      transform + agent pipeline on the api-service.
    // Mirrors `beginStructuredJob` + `uploadStructuredFile`; the
    // jobs table picks up the row immediately after step 1.
    uploadStructuredDocument: builder.mutation<
      StructuredDocumentUploadResult,
      {
        file: File
        documentType: StructuredDocumentType
        siteId?: string
        /** `VISION_PLC`: the controller id PLC ingest used, when it differs from the file stem. */
        controllerId?: string
        groupId?: string
        ingestRunAction?: 'add_site' | 'update_site'
        siteName?: string
      }
    >({
      queryFn: async (
        { file, documentType, siteId, controllerId, groupId, ingestRunAction },
        api
      ) => {
        const routeBase = STRUCTURED_DOCUMENT_TYPE_TO_ROUTE_BASE[documentType]
        if (!routeBase) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: `Unsupported structured document type: ${documentType}`
            }
          }
        }
        const beginResponse = await fetch(`${ONTOLOGY_SERVICE_URL}${routeBase}/begin`, {
          method: 'POST',
          headers: createOntologyHeaders(),
          body: JSON.stringify({
            fileName: file.name,
            ...(siteId ? { siteId } : {}),
            ...(controllerId ? { controllerId } : {}),
            ...(groupId ? { groupId } : {}),
            ...(ingestRunAction ? { ingestRunAction } : {})
          })
        })
        if (!beginResponse.ok) {
          const detail = await beginResponse.text().catch(() => '')
          return {
            error: {
              status: beginResponse.status,
              data: detail || beginResponse.statusText
            }
          }
        }
        const { jobId, branchId } = (await beginResponse.json()) as {
          jobId: string
          branchId?: string
        }
        const formData = createFormData({ file })
        // `runXhrUpload` returns `{ data } | { error }`; construct the
        // caller-facing shape from the begin response so the branch-adoption
        // listener receives the branch immediately after upload.
        const uploadResult = await runXhrUpload<{ id: string }>({
          url: `${ONTOLOGY_SERVICE_URL}${routeBase}/jobs/${encodeURIComponent(jobId)}/upload`,
          formData,
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (uploadResult.error !== undefined) {
          return finalizeFailedIngestUpload(jobId, uploadResult.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['IpMappingJobs']))
          )
        }
        return {
          data: {
            jobId,
            ...(branchId ? { branchId } : {}),
            fileName: file.name,
            documentType,
            semanticType: STRUCTURED_DOCUMENT_TYPE_TO_SEMANTIC_TYPE[documentType]
          }
        }
      },
      // Invalidate the jobs-page cache so the row appears in the
      // IngestPage immediately after the multipart `upload` request
      // resolves (the begin step has already persisted the row by then).
      invalidatesTags: ['IpMappingJobs']
    }),
    uploadWorkOrdersToneDocument: builder.mutation<WorkOrdersToneUploadStarted, { file: File }>({
      queryFn: async ({ file }, api) => {
        const beginResponse = await fetch(`${ONTOLOGY_SERVICE_URL}/work-orders/begin`, {
          method: 'POST',
          headers: createOntologyHeaders(),
          body: JSON.stringify({ fileName: file.name }),
          signal: api.signal
        })
        if (!beginResponse.ok) {
          const detail = await beginResponse.text().catch(() => '')
          return {
            error: {
              status: beginResponse.status,
              data: detail || beginResponse.statusText
            }
          }
        }
        const { jobId } = (await beginResponse.json()) as { jobId: string }
        // Surface the `uploading` row in the IngestPage right away; the upload
        // POST below returns 202 quickly (the tone ingest runs in the
        // background), so the mutation's `invalidatesTags` then refreshes it to
        // `processing` and the table's 2s poll tracks it through to `done`.
        api.dispatch(ingestApi.util.invalidateTags(['WorkOrdersJobs']))

        const formData = createFormData({ file })
        const uploadResult = await runXhrUpload<WorkOrdersToneUploadStarted>({
          url: `${ONTOLOGY_SERVICE_URL}/work-orders/jobs/${encodeURIComponent(jobId)}/upload`,
          formData,
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (uploadResult.error) {
          return finalizeFailedIngestUpload(jobId, uploadResult.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['WorkOrdersJobs']))
          )
        }
        return { data: uploadResult.data ?? { jobId } }
      },
      invalidatesTags: ['WorkOrdersJobs']
    }),
    uploadUnstructuredFiles: builder.mutation<UnstructuredJob, { jobId: string; files: File[] }>({
      queryFn: async ({ jobId, files }, api) => {
        if (files.length === 0) {
          return {
            error: { status: 'FETCH_ERROR', error: 'No files to upload' }
          }
        }

        // Shared corpus primitive (progress/abort keyed by jobId for Stop).
        const unzipResult = await uploadCorpusFilesSequentially({
          jobId,
          files,
          dispatch: api.dispatch,
          signal: api.signal,
          onUploadBytesComplete: () => {
            void notifyUnstructuredUploadSent(jobId)
          }
        })

        if (unzipResult.error) {
          const message = extractUploadErrorMessage(unzipResult.error)
          await notifyUnstructuredUploadFailed(jobId, message)
          return { error: unzipResult.error }
        }

        const unzip = unzipResult.data
        if (!unzip?.jobId) {
          await notifyUnstructuredUploadFailed(jobId, 'Upload failed: invalid proxy response')
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Upload failed: invalid proxy response'
            }
          }
        }

        // Row-level label for the IngestJob row when N>1 — keeps the
        // JobsTable fileName column informative without a schema change.
        const fileName =
          files.length === 1 ? files[0].name : `${files[0].name} + ${files.length - 1} more`

        try {
          const processResponse = await fetch(
            `${ONTOLOGY_SERVICE_URL}/ingest/unstructured/jobs/${encodeURIComponent(jobId)}/process`,
            {
              method: 'POST',
              headers: createOntologyHeaders(),
              body: JSON.stringify({ fileName, unzip }),
              signal: api.signal
            }
          )
          const processBody: unknown = await processResponse.json().catch(() => null)
          if (!processResponse.ok) {
            const message =
              processBody &&
              typeof processBody === 'object' &&
              typeof (processBody as Record<string, unknown>)['error'] === 'string'
                ? String((processBody as Record<string, unknown>)['error'])
                : `Process failed (${processResponse.status})`
            await notifyUnstructuredUploadFailed(jobId, message)
            return {
              error: { status: processResponse.status, data: processBody }
            }
          }
          return { data: processBody as UnstructuredJob }
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err)
          await notifyUnstructuredUploadFailed(jobId, message)
          return { error: { status: 'FETCH_ERROR', error: message } }
        }
      },
      invalidatesTags: ['UnstructuredJobs']
    }),
    getUnstructuredJobDetail: builder.query<UnstructuredJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'UnstructuredJobs', id: jobId }]
    }),
    // Open `ask_user_question` rows for an unstructured-ingest job. The agent
    // ingest page refetches these on agent-write wake-ups and unblocks the run
    // with an answer.
    getIngestQuestions: builder.query<IngestQuestionsResult, string>({
      query: (jobId) =>
        `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/questions?includeAnswered=true`,
      transformResponse: (response: {
        questions?: PendingQuestionWire[]
        answered?: AnsweredQuestionWire[]
        messages?: OperatorMessageWire[]
      }) => {
        const attribution = (w: PendingQuestionWire | OperatorMessageWire) => ({
          ...(w.agentKind ? { agentKind: w.agentKind } : {}),
          ...(w.params?.dispatchId ? { dispatchId: w.params.dispatchId } : {}),
          ...(w.params?.sectionId ? { sectionId: w.params.sectionId } : {}),
          ...(w.params?.agentRunId ? { agentRunId: w.params.agentRunId } : {})
        })
        // Repair Anthropic-style XML parameter markup that some model paths
        // leak into params.question (AXI-5007) so already-persisted rows still
        // render as question cards with option pills.
        const cardFields = (w: PendingQuestionWire) => {
          const normalized = normalizeAskUserQuestionFields({
            question: w.params?.question ?? w.summary ?? '',
            options: w.params?.options,
            context: w.params?.context
          })
          return {
            question: normalized.question,
            ...(normalized.options ? { options: normalized.options } : {}),
            ...(normalized.context ? { context: normalized.context } : {})
          }
        }
        return {
          pending: (response.questions ?? []).map((w) => ({
            writeId: w.id,
            ...cardFields(w),
            askedAt: w.submittedAt ?? '',
            ...attribution(w)
          })),
          answered: (response.answered ?? [])
            .map((w) => ({
              writeId: w.id,
              ...cardFields(w),
              answer: w.result?.answer ?? '',
              askedAt: w.submittedAt ?? '',
              answeredAt: w.completedAt ?? w.submittedAt ?? '',
              ...attribution(w)
            }))
            // Oldest first → newest at the bottom, chat-thread order.
            .sort((a, b) => a.answeredAt.localeCompare(b.answeredAt)),
          messages: (response.messages ?? [])
            .map((w) => toIngestOperatorMessage(w))
            .sort((a, b) => a.sentAt.localeCompare(b.sentAt))
        }
      },
      providesTags: (_result, _error, jobId) => [{ type: 'IngestQuestions', id: jobId }]
    }),
    // The agent's per-job scratchpad (markdown notes). Missing doc returns empty
    // content (the agent hasn't written yet), not a 404.
    getIngestScratchpad: builder.query<IngestScratchpad, string>({
      query: (jobId) =>
        `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/scratchpad`,
      providesTags: (_result, _error, jobId) => [{ type: 'UnstructuredJobs', id: jobId }]
    }),
    // The job's section catalog — the agent-defined workstreams the section
    // tabs render. A job with no catalog yet (pre-partition, legacy) returns [].
    listIngestSections: builder.query<IngestSection[], string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/sections`,
      transformResponse: (response: { sections?: IngestSection[] }) => response.sections ?? [],
      providesTags: (_result, _error, jobId) => [{ type: 'UnstructuredJobs', id: jobId }]
    }),
    // Per-file job inventory — coverage arithmetic for the AgentIngestPage panel.
    listIngestJobFiles: builder.query<IngestJobFile[], string>({
      query: (jobId) =>
        `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/job-files`,
      transformResponse: (response: { files?: IngestJobFile[] }) => response.files ?? [],
      providesTags: (_result, _error, jobId) => [{ type: 'UnstructuredJobs', id: jobId }]
    }),
    // Cheap write-log identity — refetch modifications/changeset only when
    // count/maxId moves (avoids Neo4j hydration on every wake-up).
    getIngestWriteLogWatermark: builder.query<IngestWriteLogWatermark, string>({
      query: (jobId) =>
        `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/write-log-watermark`,
      providesTags: (_result, _error, jobId) => [{ type: 'UnstructuredJobs', id: jobId }]
    }),
    getBranchIngestWriteLogWatermark: builder.query<
      IngestWriteLogWatermark,
      { branchId: string; domain?: 'agent' }
    >({
      query: ({ branchId, domain }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/branches/${encodeURIComponent(branchId)}/write-log-watermark`,
        params: domain ? { domain } : undefined
      }),
      providesTags: (_result, _error, { branchId }) => [{ type: 'BranchIngestPaint', id: branchId }]
    }),
    // "What it built", from the durable write log (O(modifications), no branch diff).
    // `hasLog: false` → the job predates the log; the caller falls back to compare.
    getIngestModifications: builder.query<IngestModifications, string>({
      query: (jobId) =>
        `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/modifications`,
      providesTags: (_result, _error, jobId) => [{ type: 'UnstructuredJobs', id: jobId }]
    }),
    // Property-level net changeset — the fold of the write-log deltas, scoped
    // to `sectionId` when given (job-wide otherwise). Sibling read surface to
    // `getIngestModifications`; the paint's tier-2 (old→new value rows).
    getIngestChangeset: builder.query<IngestChangeset, { jobId: string; sectionId?: string }>({
      query: ({ jobId, sectionId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/changeset`,
        params: sectionId ? { sectionId } : undefined
      }),
      providesTags: (_result, _error, { jobId }) => [{ type: 'UnstructuredJobs', id: jobId }]
    }),
    getBranchIngestModifications: builder.query<
      BranchIngestModifications,
      { branchId: string; domain?: 'agent'; sectionsJobId?: string }
    >({
      query: ({ branchId, domain, sectionsJobId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/branches/${encodeURIComponent(branchId)}/modifications`,
        params: {
          ...(domain ? { domain } : {}),
          ...(sectionsJobId ? { sectionsJobId } : {})
        }
      }),
      providesTags: (_result, _error, { branchId }) => [{ type: 'BranchIngestPaint', id: branchId }]
    }),
    getBranchIngestChangeset: builder.query<
      BranchIngestChangeset,
      {
        branchId: string
        domain?: 'agent'
        sectionId?: string
        sectionsJobId?: string
      }
    >({
      query: ({ branchId, domain, sectionId, sectionsJobId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/branches/${encodeURIComponent(branchId)}/changeset`,
        params: {
          ...(domain ? { domain } : {}),
          ...(sectionId ? { sectionId } : {}),
          ...(sectionsJobId ? { sectionsJobId } : {})
        }
      }),
      providesTags: (_result, _error, { branchId }) => [{ type: 'BranchIngestPaint', id: branchId }]
    }),
    // One section's scratchpad (its parser's durable notes). Same empty-case
    // semantics as the job-level doc.
    getIngestSectionScratchpad: builder.query<
      IngestScratchpad,
      { jobId: string; sectionId: string }
    >({
      query: ({ jobId, sectionId }) =>
        `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/sections/${encodeURIComponent(sectionId)}/scratchpad`,
      providesTags: (_result, _error, { jobId }) => [{ type: 'UnstructuredJobs', id: jobId }]
    }),
    // The ingest run's persisted transcript (agent_conversations tree), keyed
    // by the job's conversationId. Conversations publish no wake-up, so it
    // refetches on `jobs` wake-ups from the same run and when the session
    // pane's chain-of-thought stream commits a turn. Reuses the orchestrator
    // conversation read endpoint + its `ProxyConversationWithRuns` wire shape.
    getIngestConversation: builder.query<ProxyConversationWithRuns, string>({
      query: (conversationId) =>
        `${ONTOLOGY_SERVICE_URL}/agent-conversations/${encodeURIComponent(conversationId)}`,
      providesTags: (_result, _error, conversationId) => [
        { type: 'IngestConversation', id: conversationId }
      ]
    }),
    answerIngestQuestion: builder.mutation<
      { ok: boolean },
      { jobId: string; writeId: string; answer: string }
    >({
      query: ({ jobId, writeId, answer }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/questions/${encodeURIComponent(writeId)}/answer`,
        method: 'POST',
        body: { answer }
      }),
      // Clear the answered question and let the job-detail poll pick up the
      // resumed run's next logs.
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: 'IngestQuestions', id: jobId },
        { type: 'UnstructuredJobs', id: jobId }
      ]
    }),
    // Operator → agent: queue an unsolicited message a running ingest agent
    // drains at its next turn. Optimistically insert a queued bubble, then
    // reconcile against the durable write (and invalidate so polls stay
    // authoritative).
    sendIngestMessage: builder.mutation<
      SendIngestMessageResult,
      {
        jobId: string
        message: string
        dispatchId?: string
        agentKind?: 'ingest_section_parser' | 'ingest_reconciler'
      }
    >({
      query: ({ jobId, message, dispatchId, agentKind }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/messages`,
        method: 'POST',
        body: {
          message,
          ...(dispatchId ? { dispatchId } : {}),
          ...(agentKind ? { agentKind } : {})
        }
      }),
      async onQueryStarted(
        { jobId, message, dispatchId, agentKind },
        { dispatch, queryFulfilled }
      ) {
        const tempId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        const sentAt = new Date().toISOString()
        const patch = dispatch(
          ingestApi.util.updateQueryData('getIngestQuestions', jobId, (draft) => {
            draft.messages.push({
              writeId: tempId,
              message,
              sentAt,
              pending: true,
              ...(dispatchId ? { dispatchId } : {}),
              ...(agentKind ? { agentKind } : {})
            })
          })
        )
        try {
          const { data } = await queryFulfilled
          if (!data.write) return
          const reconciled = toIngestOperatorMessage(data.write, message)
          dispatch(
            ingestApi.util.updateQueryData('getIngestQuestions', jobId, (draft) => {
              const idx = draft.messages.findIndex((m) => m.writeId === tempId)
              if (idx >= 0) {
                draft.messages[idx] = reconciled
              } else if (!draft.messages.some((m) => m.writeId === reconciled.writeId)) {
                draft.messages.push(reconciled)
              }
              draft.messages.sort((a, b) => a.sentAt.localeCompare(b.sentAt))
            })
          )
        } catch {
          patch.undo()
        }
      },
      // Invalidate the job detail too: a message to a finished run re-dispatches
      // it, so the job flips back to a non-terminal state and the detail poll
      // (and the composer's active state) must pick that up.
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: 'IngestQuestions', id: jobId },
        { type: 'UnstructuredJobs', id: jobId }
      ]
    }),
    // Cancel a still-queued operator message (deletes the un-consumed row).
    cancelIngestMessage: builder.mutation<{ ok: boolean }, { jobId: string; writeId: string }>({
      query: ({ jobId, writeId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/messages/${encodeURIComponent(writeId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { jobId }) => [{ type: 'IngestQuestions', id: jobId }]
    }),
    // Force: interrupt the live run (non-reaping) so it parks + re-dispatches,
    // delivering the queued message(s) on the fresh run's first turn. With
    // `dispatchId`, resume that parked child instead.
    forceIngestMessage: builder.mutation<{ ok: boolean }, { jobId: string; dispatchId?: string }>({
      query: ({ jobId, dispatchId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/messages/force`,
        method: 'POST',
        body: dispatchId ? { dispatchId } : {}
      }),
      invalidatesTags: (_result, _error, { jobId }) => [{ type: 'IngestQuestions', id: jobId }]
    }),
    // Same /jobs/:id endpoint as the other detail queries, but tagged
    // under `IpMappingJobs` so the per-job polling subscription is
    // invalidated by cancel/list mutations that target that tag type.
    // The IngestPage's detail panel uses this to stream `logs` for the
    // running agent (match calls, cypher_read previews, end-of-run
    // summary). The wire payload is the unified `IngestJob` shape;
    // the narrower `IpMappingJob` projection is all the UI
    // needs.
    getIpMappingJobDetail: builder.query<IpMappingJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'IpMappingJobs', id: jobId }]
    }),
    getWorkOrdersJobDetail: builder.query<WorkOrdersJob, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/jobs/${jobId}`,
      providesTags: (_result, _error, jobId) => [{ type: 'WorkOrdersJobs', id: jobId }]
    }),
    discardStructuredDiff: builder.mutation<{ success: boolean }, string>({
      query: (jobId) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/discard`,
        method: 'POST'
      }),
      invalidatesTags: ['StructuredJobs']
    }),
    cancelIngestJob: builder.mutation<
      JobListItem,
      string | { jobId: string; scope?: 'job' | 'group' }
    >({
      query: (input) => {
        const jobId = typeof input === 'string' ? input : input.jobId
        const scope = typeof input === 'string' ? undefined : input.scope
        return {
          url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/cancel`,
          method: 'POST',
          ...(scope ? { body: { scope } } : {})
        }
      },
      // Invalidating the string list tags also invalidates any
      // `{ type, id }` subscriptions sharing that tag type (e.g.
      // expanded-panel detail polls). Listing every surviving kind
      // covers all per-job detail subscriptions symmetrically.
      invalidatesTags: [
        'StructuredJobs',
        'PlcJobs',
        'RoboticsJobs',
        'VisionJobs',
        'MTConnectJobs',
        'PIJobs',
        'CanaryJobs',
        'ExperionJobs',
        'SrsJobs',
        'SafetyManagerJobs',
        'TagMappingJobs',
        'UnstructuredJobs',
        'IpMappingJobs',
        'TagImportJobs',
        'TabularDumpJobs',
        'SqlJobs',
        'SoapJobs',
        'PssJobs',
        'MappedTabularIngestJobs',
        'TabularConfigBuilds'
      ]
    }),
    stopIngestJob: builder.mutation<JobListItem, string>({
      query: (jobId) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/stop`,
        method: 'POST'
      }),
      invalidatesTags: [
        'StructuredJobs',
        'PlcJobs',
        'RoboticsJobs',
        'VisionJobs',
        'MTConnectJobs',
        'PIJobs',
        'CanaryJobs',
        'ExperionJobs',
        'SrsJobs',
        'SafetyManagerJobs',
        'TagMappingJobs',
        'UnstructuredJobs',
        'IpMappingJobs',
        'TagImportJobs',
        'TabularDumpJobs',
        'SqlJobs',
        'SoapJobs',
        'PssJobs',
        'MappedTabularIngestJobs',
        'TabularConfigBuilds'
      ]
    }),
    // Stops the job if it hasn't finished, closes its open questions, then
    // deletes the row and the branch the job owns. A 409 `JOB_RECORD_PRESERVED`
    // means the row was kept because its documents are in another branch.
    deleteIngestJobRecord: builder.mutation<void, string>({
      query: (jobId) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: [
        'StructuredJobs',
        'PlcJobs',
        'RoboticsJobs',
        'VisionJobs',
        'MTConnectJobs',
        'PIJobs',
        'CanaryJobs',
        'ExperionJobs',
        'SrsJobs',
        'SafetyManagerJobs',
        'TagMappingJobs',
        'UnstructuredJobs',
        'IpMappingJobs',
        'TagImportJobs',
        'TabularDumpJobs',
        'SqlJobs',
        'SoapJobs',
        'PssJobs',
        'MappedTabularIngestJobs',
        'TabularConfigBuilds',
        'WorkOrdersJobs'
      ]
    })
  })
})

export const {
  useGetJobLogsPageQuery,
  useListIngestIntakesQuery,
  useCreateIngestIntakeMutation,
  usePatchIngestIntakeMutation,
  useDeleteIngestIntakeMutation,
  useListIngestIntakeItemsQuery,
  useLazyListIngestIntakeItemsQuery,
  useRunIngestIntakeItemMutation,
  useSkipIngestIntakeItemMutation,
  useConfirmIngestIntakeItemMutation,
  useListTabularFileNameMatchesQuery,
  useBeginStructuredJobMutation,
  useUploadStructuredFileMutation,
  useBeginTagImportJobMutation,
  useUploadTagImportFileMutation,
  useGetTagImportJobDetailQuery,
  useLazyGetTagImportErrorCsvQuery,
  useLazyGetTagImportTemplateQuery,
  useCommitTagImportJobMutation,
  useCancelTagImportJobMutation,
  useBeginTabularDumpJobMutation,
  usePreviewTabularDumpMutation,
  useUploadTabularDumpFileMutation,
  useGetTabularDumpJobDetailQuery,
  useConnectSqlMutation,
  useGetSqlJobDetailQuery,
  usePreviewSoapMutation,
  useConnectSoapMutation,
  useGetSoapJobDetailQuery,
  useDiscoverPssMutation,
  useGetPssJobDetailQuery,
  useBeginAvevaGraphicsJobMutation,
  useUploadAvevaGraphicsFileMutation,
  useGetStructuredJobsQuery,
  useGetStructuredJobDetailQuery,
  useGetJobDiffPreviewQuery,
  useCheckExistingGatewayQuery,
  useLazyCheckExistingGatewayQuery,
  useCheckStorageStatusQuery,
  useLazyCheckStorageStatusQuery,
  useGetIngestQuestionsQuery,
  useGetIngestScratchpadQuery,
  useListIngestSectionsQuery,
  useListIngestJobFilesQuery,
  useListSiteDocumentsQuery,
  useListSiteUnstructuredFilesQuery,
  useDeleteIngestedFileMutation,
  useGetIngestWriteLogWatermarkQuery,
  useGetBranchIngestWriteLogWatermarkQuery,
  useGetIngestModificationsQuery,
  useGetIngestChangesetQuery,
  useGetBranchIngestModificationsQuery,
  useGetBranchIngestChangesetQuery,
  useGetIngestSectionScratchpadQuery,
  useGetIngestConversationQuery,
  useAnswerIngestQuestionMutation,
  useSendIngestMessageMutation,
  useCancelIngestMessageMutation,
  useForceIngestMessageMutation,
  useBeginStructuredDiffJobMutation,
  usePreviewStructuredDiffMutation,
  useApplyStructuredDiffMutation,
  useGetIngestJobsPageQuery,
  useRenameIngestRunMutation,
  useBeginPlcJobMutation,
  useUploadPlcFileMutation,
  useGetPlcJobsQuery,
  useGetPlcJobDetailQuery,
  useBeginRoboticsJobMutation,
  useUploadRoboticsFileMutation,
  useBeginVisionJobMutation,
  useUploadVisionFileMutation,
  useGetVisionJobDetailQuery,
  useGetRoboticsJobsQuery,
  useGetRoboticsJobDetailQuery,
  useConnectMTConnectAgentMutation,
  useGetMTConnectJobDetailQuery,
  useConnectPIMutation,
  useSuggestPIHistoryMappingMutation,
  usePreviewPIHistoryMappingMutation,
  useGetPIJobDetailQuery,
  useConnectCanaryMutation,
  useSuggestCanaryHistoryMappingMutation,
  usePreviewCanaryHistoryMappingMutation,
  useGetCanaryJobDetailQuery,
  useConnectExperionErdbMutation,
  useGetExperionJobDetailQuery,
  useGetSrsJobDetailQuery,
  useBeginSrsJobMutation,
  useUploadSrsFileMutation,
  useGetSafetyManagerJobDetailQuery,
  useBeginSafetyManagerJobMutation,
  useConnectSafetyManagerDbMutation,
  useUploadSafetyManagerFileMutation,
  useListTagMappingTypesQuery,
  usePreviewTagMappingMutation,
  useSuggestTagMappingMutation,
  useRunTagMappingMutation,
  useGetTagMappingJobDetailQuery,
  useBeginUnstructuredJobMutation,
  useBeginDocumentAdditionMutation,
  useUploadUnstructuredCorpusMutation,
  useCompleteDocumentAdditionMutation,
  useAbortDocumentAdditionMutation,
  useUploadStructuredDocumentMutation,
  useUploadWorkOrdersToneDocumentMutation,
  useUploadUnstructuredFilesMutation,
  useGetUnstructuredJobDetailQuery,
  useGetIpMappingJobDetailQuery,
  useGetWorkOrdersJobDetailQuery,
  useDiscardStructuredDiffMutation,
  useCancelIngestJobMutation,
  useStopIngestJobMutation,
  useDeleteIngestJobRecordMutation
} = ingestApi
